import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
// Import Material-UI TextField component
import TextField from '@mui/material/TextField';
// Reverted import for createTheme to its standard form
import { ThemeProvider, createTheme } from '@mui/material/styles';


// Helper function to shuffle an array (Fisher-Yates algorithm)
const shuffleArray = (array) => {
  let currentIndex = array.length, randomIndex;
  while (currentIndex !== 0) {
    randomIndex = Math.floor(RandoM.random() * currentIndex); // Use RandoM.random()
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
  }
  return array;
};

// Create a custom MUI theme to match the dark aesthetic and text colors
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#8b5cf6', // A purple shade
    },
    secondary: {
      main: '#ec4899', // A pink shade
    },
    text: {
      primary: '#e2e8f0', // Tailwind gray-200
      secondary: '#94a3b8', // Tailwind gray-400
    },
    background: {
      default: '#1f2937', // Tailwind gray-800
      paper: '#374151',   // Tailwind gray-700
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          // You can put root styling here if needed, but Tailwind classes often override it
        },
      },
      defaultProps: {
        variant: 'outlined', // Default to outlined for a cleaner look
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '0.5rem', // rounded-lg
          '& fieldset': {
            borderColor: '#4b5563', // Tailwind gray-600 for border
          },
          '&:hover fieldset': {
            borderColor: '#a78bfa !important', // Purple-400 on hover
          },
          '&.Mui-focused fieldset': {
            borderColor: '#8b5cf6 !important', // Purple-500 on focus
          },
        },
        input: {
          color: '#e2e8f0', // Tailwind gray-200 for input text
          '&::placeholder': {
            color: '#94a3b8', // Tailwind gray-400 for placeholder
            opacity: 1, // Fix for some browsers making placeholder transparent
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#94a3b8', // Tailwind gray-400 for label
          '&.Mui-focused': {
            color: '#8b5cf6', // Purple-500 on focus
          },
        },
      },
    },
  },
});

// --- Default/Bundled Smash Up Data (with a version) ---
// This serves as the initial data if fetching from URL fails or is slower.
// Make sure this matches the structure of your remote JSON, including a 'version' field.
// This is kept as a fallback, but the primary initialization will come from fetched data or localStorage.
const DEFAULT_SMASH_UP_DATA = {
  "version": 1.20,
  "sets": [
    {
      "id": 1,
      "name": "Core Set",
      "type": "base",
      "status": "active",
      "releaseDate": "2012-08-07",
      "factionCount": 8,
      "description": "The original base game with 8 factions"
    },
    {
      "id": 2,
      "name": "Awesome Level 9000",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2013-02-14",
      "factionCount": 4,
      "description": "First expansion with 4 new factions"
    },
    {
      "id": 3,
      "name": "The Obligatory Cthulhu Set",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2013-10-25",
      "factionCount": 4,
      "description": "Lovecraftian themed expansion"
    },
    {
      "id": 4,
      "name": "Science Fiction Double Feature",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2014-04-11",
      "factionCount": 4,
      "description": "Sci-fi themed expansion"
    },
    {
      "id": 5,
      "name": "Monster Smash",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2014-10-15",
      "factionCount": 4,
      "description": "Classic monster themed expansion"
    },
    {
      "id": 6,
      "name": "Pretty Pretty Smash Up",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2015-03-11",
      "factionCount": 4,
      "description": "Fantasy/cute themed expansion"
    },
    {
      "id": 7,
      "name": "Smash Up: Munchkin",
      "type": "standalone",
      "status": "active",
      "releaseDate": "2015-07-29",
      "factionCount": 8,
      "description": "Munchkin crossover standalone game"
    },
    {
      "id": 8,
      "name": "It's Your Fault!",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2016-02-10",
      "factionCount": 5,
      "description": "Fan-voted factions expansion"
    },
    {
      "id": 9,
      "name": "Cease and Desist",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2016-07-13",
      "factionCount": 4,
      "description": "Parody factions expansion"
    },
    {
      "id": 10,
      "name": "What Were We Thinking?",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2017-02-15",
      "factionCount": 4,
      "description": "Unusual theme combinations"
    },
    {
      "id": 11,
      "name": "Big Geeky Box",
      "type": "storage",
      "status": "discontinued",
      "releaseDate": "2017-07-19",
      "factionCount": 1,
      "description": "Storage solution with Geeks faction"
    },
    {
      "id": 12,
      "name": "Big in Japan",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2017-10-25",
      "factionCount": 4,
      "description": "Japanese pop culture themed"
    },
    {
      "id": 13,
      "name": "All Stars Event Kit",
      "type": "promo",
      "status": "discontinued",
      "releaseDate": "2018-01-01",
      "factionCount": 1,
      "description": "Tournament promotional faction"
    },
    {
      "id": 14,
      "name": "Smash Up Sheep Promo",
      "type": "promo",
      "status": "discontinued",
      "releaseDate": "2018-03-01",
      "factionCount": 1,
      "description": "Single faction promotional pack"
    },
    {
      "id": 15,
      "name": "That '70s Expansion",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2018-07-11",
      "factionCount": 4,
      "description": "1970s pop culture themed"
    },
    {
      "id": 16,
      "name": "The Bigger Geekier Box",
      "type": "storage",
      "status": "active",
      "releaseDate": "2018-10-17",
      "factionCount": 2,
      "description": "Improved storage with Geeks and All Stars"
    },
    {
      "id": 17,
      "name": "Oops, You Did It Again",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2019-02-13",
      "factionCount": 4,
      "description": "Historical warriors expansion"
    },
    {
      "id": 18,
      "name": "World Tour: International Incident",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2019-07-10",
      "factionCount": 4,
      "description": "International themed factions"
    },
    {
      "id": 19,
      "name": "Smash Up Penguins",
      "type": "promo",
      "status": "active",
      "releaseDate": "2019-11-01",
      "factionCount": 1,
      "description": "Single faction promotional pack"
    },
    {
      "id": 20,
      "name": "Smash Up TITANS",
      "type": "promo",
      "status": "active",
      "releaseDate": "2019-11-01",
      "factionCount": 0,
      "description": "Titan cards for existing factions"
    },
    {
      "id": 21,
      "name": "TITANS Event Kit",
      "type": "promo",
      "status": "discontinued",
      "releaseDate": "2019-11-01",
      "factionCount": 0,
      "description": "Tournament promotional titans"
    },
    {
      "id": 22,
      "name": "World Tour: Culture Shock",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2020-02-12",
      "factionCount": 5,
      "description": "Cultural mythology themed"
    },
    {
      "id": 23,
      "name": "Dead Reckoning Promo",
      "type": "promo",
      "status": "active",
      "releaseDate": "2020-07-01",
      "factionCount": 0,
      "description": "Promotional base"
    },
    {
      "id": 24,
      "name": "Smash Up: Marvel",
      "type": "standalone",
      "status": "active",
      "releaseDate": "2020-10-07",
      "factionCount": 8,
      "description": "Marvel Comics themed standalone"
    },
    {
      "id": 25,
      "name": "Smash Up Goblins",
      "type": "promo",
      "status": "active",
      "releaseDate": "2021-03-01",
      "factionCount": 1,
      "description": "Single faction promotional pack"
    },
    {
      "id": 26,
      "name": "Smash Up Knights of the Round Table",
      "type": "promo",
      "status": "active",
      "releaseDate": "2021-08-01",
      "factionCount": 1,
      "description": "Single faction promotional pack"
    },
    {
      "id": 27,
      "name": "Smash Up: Disney Edition",
      "type": "standalone",
      "status": "active",
      "releaseDate": "2022-03-09",
      "factionCount": 8,
      "description": "Disney themed standalone game"
    },
    {
      "id": 28,
      "name": "10th Anniversary",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2022-08-01",
      "factionCount": 5,
      "description": "Anniversary edition with fan favorites"
    },
    {
      "id": 29,
      "name": "Excellent Movies, Dudes!",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2023-02-15",
      "factionCount": 4,
      "description": "80s/90s movie parodies"
    },
    {
      "id": 30,
      "name": "Smash Up Teens",
      "type": "promo",
      "status": "active",
      "releaseDate": "2023-08-01",
      "factionCount": 1,
      "description": "Single faction promotional pack"
    },
    {
      "id": 31,
      "name": "Half the Battle",
      "type": "expansion",
      "status": "active",
      "releaseDate": "2025-08-26",
      "factionCount": 4,
      "description": "80s action cartoon parodies"
    },
        {
      "id": 32,
      "name": "Smash Up: Slashers",
      "type": "promo",
      "status": "active",
      "releaseDate": "2023-08-26",
      "factionCount": 1,
      "description": "Single faction promotional pack"
    },
    {
      "id": 33,
      "name": "Smash Up: Clowns",
      "type": "promo",
      "status": "active",
      "releaseDate": "2025-08-26",
      "factionCount": 1,
      "description": "Single faction promotional pack"
    }
  ],
  "factions": [
    {
      "id": 1,
      "name": "Aliens",
      "setId": 1,
      "theme": "Sci-Fi",
      "description": "Return minions to hands and manipulate bases"
    },
    {
      "id": 2,
      "name": "Dinosaurs",
      "setId": 1,
      "theme": "Prehistoric",
      "description": "High-powered minions and destruction"
    },
    {
      "id": 3,
      "name": "Ninjas",
      "setId": 1,
      "theme": "Martial Arts",
      "description": "Destroy cards and sneaky tactics"
    },
    {
      "id": 4,
      "name": "Pirates",
      "setId": 1,
      "theme": "Maritime",
      "description": "Movement and weak minion destruction"
    },
    {
      "id": 5,
      "name": "Robots",
      "setId": 1,
      "theme": "Sci-Fi",
      "description": "Extra minion plays and swarm tactics"
    },
    {
      "id": 6,
      "name": "Tricksters",
      "setId": 1,
      "theme": "Chaos",
      "description": "Annoyance and base manipulation"
    },
    {
      "id": 7,
      "name": "Wizards",
      "setId": 1,
      "theme": "Magic",
      "description": "Card draw and action play"
    },
    {
      "id": 8,
      "name": "Zombies",
      "setId": 1,
      "theme": "Horror",
      "description": "Discard pile recursion"
    },
    {
      "id": 9,
      "name": "Bear Cavalry",
      "setId": 2,
      "theme": "Fantasy",
      "description": "Minion movement and destruction"
    },
    {
      "id": 10,
      "name": "Ghosts",
      "setId": 2,
      "theme": "Horror",
      "description": "Benefits from discarding and small hand size"
    },
    {
      "id": 11,
      "name": "Killer Plants",
      "setId": 2,
      "theme": "Nature",
      "description": "Extra minions from deck and delayed effects"
    },
    {
      "id": 12,
      "name": "Steampunks",
      "setId": 2,
      "theme": "Steampunk",
      "description": "Play-on-base action synergy"
    },
    {
      "id": 13,
      "name": "Elder Things",
      "setId": 3,
      "theme": "Lovecraftian",
      "description": "Madness penalties for opponents"
    },
    {
      "id": 14,
      "name": "Innsmouth",
      "setId": 3,
      "theme": "Lovecraftian",
      "description": "Same-name minion benefits"
    },
    {
      "id": 15,
      "name": "Minions of Cthulhu",
      "setId": 3,
      "theme": "Lovecraftian",
      "description": "Powerful actions with Madness costs"
    },
    {
      "id": 16,
      "name": "Miskatonic University",
      "setId": 3,
      "theme": "Lovecraftian",
      "description": "Madness manipulation and benefits"
    },
    {
      "id": 17,
      "name": "Cyborg Apes",
      "setId": 4,
      "theme": "Sci-Fi",
      "description": "Play-on-minion action enhancement"
    },
    {
      "id": 18,
      "name": "Shapeshifters",
      "setId": 4,
      "theme": "Sci-Fi",
      "description": "Copy abilities and minion replacement"
    },
    {
      "id": 19,
      "name": "Super Spies",
      "setId": 4,
      "theme": "Espionage",
      "description": "Deck manipulation and forced discards"
    },
    {
      "id": 20,
      "name": "Time Travelers",
      "setId": 4,
      "theme": "Sci-Fi",
      "description": "Ability repetition and card retrieval"
    },
    {
      "id": 21,
      "name": "Giant Ants",
      "setId": 5,
      "theme": "Horror",
      "description": "Power counter generation and transfer"
    },
    {
      "id": 22,
      "name": "Mad Scientists",
      "setId": 5,
      "theme": "Horror",
      "description": "Power counter manipulation"
    },
    {
      "id": 23,
      "name": "Vampires",
      "setId": 5,
      "theme": "Horror",
      "description": "Minion destruction and power counters"
    },
    {
      "id": 24,
      "name": "Werewolves",
      "setId": 5,
      "theme": "Horror",
      "description": "Temporary power boosts and high-power benefits"
    },
    {
      "id": 25,
      "name": "Fairies",
      "setId": 6,
      "theme": "Fantasy",
      "description": "Choice effects and action manipulation"
    },
    {
      "id": 26,
      "name": "Kitty Cats",
      "setId": 6,
      "theme": "Cute",
      "description": "Minion weakening and control"
    },
    {
      "id": 27,
      "name": "Mythic Horses",
      "setId": 6,
      "theme": "Fantasy",
      "description": "Same-base minion benefits"
    },
    {
      "id": 28,
      "name": "Princesses",
      "setId": 6,
      "theme": "Fantasy",
      "description": "Power-5 minions with support cards"
    },
    {
      "id": 29,
      "name": "Clerics",
      "setId": 7,
      "theme": "Fantasy",
      "description": "Card retrieval and minion movement"
    },
    {
      "id": 30,
      "name": "Dwarves",
      "setId": 7,
      "theme": "Fantasy",
      "description": "Treasure manipulation"
    },
    {
      "id": 31,
      "name": "Elves",
      "setId": 7,
      "theme": "Fantasy",
      "description": "Mutual benefits with opponents"
    },
    {
      "id": 32,
      "name": "Halflings",
      "setId": 7,
      "theme": "Fantasy",
      "description": "Minion swarm tactics"
    },
    {
      "id": 33,
      "name": "Mages",
      "setId": 7,
      "theme": "Fantasy",
      "description": "Discard for benefits"
    },
    {
      "id": 34,
      "name": "Orcs",
      "setId": 7,
      "theme": "Fantasy",
      "description": "Action immunity and power/numbers advantage"
    },
    {
      "id": 35,
      "name": "Thieves",
      "setId": 7,
      "theme": "Fantasy",
      "description": "Treasure manipulation and stealing"
    },
    {
      "id": 36,
      "name": "Warriors",
      "setId": 7,
      "theme": "Fantasy",
      "description": "Monster manipulation"
    },
    {
      "id": 37,
      "name": "Dragons",
      "setId": 8,
      "theme": "Fantasy",
      "description": "Base manipulation and area denial"
    },
    {
      "id": 38,
      "name": "Mythic Greeks",
      "setId": 8,
      "theme": "Mythology",
      "description": "Action-triggered power boosts"
    },
    {
      "id": 39,
      "name": "Sharks",
      "setId": 8,
      "theme": "Nature",
      "description": "Movement and destruction for benefits"
    },
    {
      "id": 40,
      "name": "Superheroes",
      "setId": 8,
      "theme": "Comics",
      "description": "Minion protection and morphing"
    },
    {
      "id": 41,
      "name": "Tornados",
      "setId": 8,
      "theme": "Weather",
      "description": "Universal minion movement"
    },
    {
      "id": 42,
      "name": "Astroknights",
      "setId": 9,
      "theme": "Parody",
      "description": "Unique minions with action focus"
    },
    {
      "id": 43,
      "name": "Changerbots",
      "setId": 9,
      "theme": "Parody",
      "description": "Form switching and movement"
    },
    {
      "id": 44,
      "name": "Ignobles",
      "setId": 9,
      "theme": "Parody",
      "description": "Minion control trading"
    },
    {
      "id": 45,
      "name": "Star Roamers",
      "setId": 9,
      "theme": "Parody",
      "description": "Minion return mechanics"
    },
    {
      "id": 46,
      "name": "Explorers",
      "setId": 10,
      "theme": "Adventure",
      "description": "Base manipulation and movement"
    },
    {
      "id": 47,
      "name": "Grannies",
      "setId": 10,
      "theme": "Humor",
      "description": "Deck manipulation from both ends"
    },
    {
      "id": 48,
      "name": "Rock Stars",
      "setId": 10,
      "theme": "Music",
      "description": "Groupie collection and mass movement"
    },
    {
      "id": 49,
      "name": "Teddy Bears",
      "setId": 10,
      "theme": "Cute",
      "description": "Extra low-power minions and neutralization"
    },
    {
      "id": 50,
      "name": "Geeks",
      "setId": 16,
      "theme": "Meta",
      "description": "Game knowledge disruption"
    },
    {
      "id": 51,
      "name": "Smash Up All Stars",
      "setId": 16,
      "theme": "Meta",
      "description": "Card optimization and retrieval"
    },
    {
      "id": 52,
      "name": "Itty Critters",
      "setId": 12,
      "theme": "Japanese",
      "description": "Temporary minion summoning"
    },
    {
      "id": 53,
      "name": "Kaiju",
      "setId": 12,
      "theme": "Japanese",
      "description": "Fewer minions but giant titans"
    },
    {
      "id": 54,
      "name": "Magical Girls",
      "setId": 12,
      "theme": "Japanese",
      "description": "Same-base minion synergy"
    },
    {
      "id": 55,
      "name": "Mega Troopers",
      "setId": 12,
      "theme": "Japanese",
      "description": "Base scoring specials"
    },
    {
      "id": 56,
      "name": "Sheep",
      "setId": 28,
      "theme": "Animals",
      "description": "Movement and action copying"
    },
    {
      "id": 57,
      "name": "Disco Dancers",
      "setId": 15,
      "theme": "1970s",
      "description": "Action effect duplication"
    },
    {
      "id": 58,
      "name": "Kung Fu Fighters",
      "setId": 15,
      "theme": "1970s",
      "description": "Power counter manipulation"
    },
    {
      "id": 59,
      "name": "Truckers",
      "setId": 15,
      "theme": "1970s",
      "description": "Play-on-base action manipulation"
    },
    {
      "id": 60,
      "name": "Vigilantes",
      "setId": 15,
      "theme": "1970s",
      "description": "Powerful team of seven minions"
    },
    {
      "id": 61,
      "name": "Ancient Egyptians",
      "setId": 17,
      "theme": "Historical",
      "description": "Bury and uncover mechanics"
    },
    {
      "id": 62,
      "name": "Cowboys",
      "setId": 17,
      "theme": "Historical",
      "description": "Duel mechanics and power boosts"
    },
    {
      "id": 63,
      "name": "Samurai",
      "setId": 17,
      "theme": "Historical",
      "description": "Benefits from minion death"
    },
    {
      "id": 64,
      "name": "Vikings",
      "setId": 17,
      "theme": "Historical",
      "description": "Card stealing and deck manipulation"
    },
    {
      "id": 65,
      "name": "Luchadors",
      "setId": 18,
      "theme": "Wrestling",
      "description": "Set-up actions on opponents"
    },
    {
      "id": 66,
      "name": "Mounties",
      "setId": 18,
      "theme": "Canadian",
      "description": "Movement to opponent bases"
    },
    {
      "id": 67,
      "name": "Musketeers",
      "setId": 18,
      "theme": "French",
      "description": "Action chaining"
    },
    {
      "id": 68,
      "name": "Sumo Wrestlers",
      "setId": 18,
      "theme": "Japanese",
      "description": "Discard for power and movement"
    },
    {
      "id": 69,
      "name": "Penguins",
      "setId": 19,
      "theme": "Animals",
      "description": "Random minion summoning"
    },
    {
      "id": 70,
      "name": "Anansi Tales",
      "setId": 22,
      "theme": "African",
      "description": "Card sharing mechanics"
    },
    {
      "id": 71,
      "name": "Ancient Incas",
      "setId": 22,
      "theme": "South American",
      "description": "Play-on-base action focus"
    },
    {
      "id": 72,
      "name": "Grimms' Fairy Tales",
      "setId": 22,
      "theme": "European",
      "description": "Paired minion mechanics"
    },
    {
      "id": 73,
      "name": "Polynesian Voyagers",
      "setId": 22,
      "theme": "Pacific",
      "description": "Base exploration and play-on-minion actions"
    },
    {
      "id": 74,
      "name": "Russian Fairy Tales",
      "setId": 22,
      "theme": "European",
      "description": "Minion replacement mechanics"
    },
    {
      "id": 75,
      "name": "Avengers",
      "setId": 24,
      "theme": "Marvel",
      "description": "Power-5 characters with paired actions"
    },
    {
      "id": 76,
      "name": "Hydra",
      "setId": 24,
      "theme": "Marvel",
      "description": "Character destruction for swarm"
    },
    {
      "id": 77,
      "name": "Kree",
      "setId": 24,
      "theme": "Marvel",
      "description": "Multiple action benefits"
    },
    {
      "id": 78,
      "name": "Masters of Evil",
      "setId": 24,
      "theme": "Marvel",
      "description": "Victory point objectives"
    },
    {
      "id": 79,
      "name": "S.H.I.E.L.D.",
      "setId": 24,
      "theme": "Marvel",
      "description": "Same-base character bonuses"
    },
    {
      "id": 80,
      "name": "Sinister Six",
      "setId": 24,
      "theme": "Marvel",
      "description": "Base modifier manipulation"
    },
    {
      "id": 81,
      "name": "Spider-Verse",
      "setId": 24,
      "theme": "Marvel",
      "description": "Dual play options"
    },
    {
      "id": 82,
      "name": "Ultimates",
      "setId": 24,
      "theme": "Marvel",
      "description": "Movement and convergence"
    },
    {
      "id": 83,
      "name": "Goblins",
      "setId": 25,
      "theme": "Fantasy",
      "description": "Coin flip mechanics"
    },
    {
      "id": 84,
      "name": "Knights of the Round Table",
      "setId": 26,
      "theme": "Arthurian",
      "description": "Movement and play-on-base synergy"
    },
    {
      "id": 85,
      "name": "Aladdin",
      "setId": 27,
      "theme": "Disney",
      "description": "Action draw and Wish cards"
    },
    {
      "id": 86,
      "name": "Beauty and the Beast",
      "setId": 27,
      "theme": "Disney",
      "description": "Discard benefits"
    },
    {
      "id": 87,
      "name": "Big Hero 6",
      "setId": 27,
      "theme": "Disney",
      "description": "Power counter synergy team"
    },
    {
      "id": 88,
      "name": "Frozen",
      "setId": 27,
      "theme": "Disney",
      "description": "Character fetching team"
    },
    {
      "id": 89,
      "name": "Mulan",
      "setId": 27,
      "theme": "Disney",
      "description": "Power counter triggered abilities"
    },
    {
      "id": 90,
      "name": "The Lion King",
      "setId": 27,
      "theme": "Disney",
      "description": "Character replacement and Mufasa synergy"
    },
    {
      "id": 91,
      "name": "The Nightmare Before Christmas",
      "setId": 27,
      "theme": "Disney",
      "description": "Character modifier manipulation"
    },
    {
      "id": 92,
      "name": "Wreck-It Ralph",
      "setId": 27,
      "theme": "Disney",
      "description": "Base modifier and movement"
    },
    {
      "id": 93,
      "name": "Mermaids",
      "setId": 28,
      "theme": "Fantasy",
      "description": "Opponent minion movement and power reduction"
    },
    {
      "id": 94,
      "name": "Skeletons",
      "setId": 28,
      "theme": "Horror",
      "description": "Bury minions with power counter boosts"
    },
    {
      "id": 95,
      "name": "World Champs",
      "setId": 28,
      "theme": "Sports",
      "description": "Card draw and power boosts"
    },
    {
      "id": 96,
      "name": "Action Heroes",
      "setId": 29,
      "theme": "80s Movies",
      "description": "Solo minion benefits"
    },
    {
      "id": 97,
      "name": "Backtimers",
      "setId": 29,
      "theme": "80s Movies",
      "description": "Stasis card mechanics"
    },
    {
      "id": 98,
      "name": "Extramorphs",
      "setId": 29,
      "theme": "80s Movies",
      "description": "Power-based deck summoning"
    },
    {
      "id": 99,
      "name": "Wraithrustlers",
      "setId": 29,
      "theme": "80s Movies",
      "description": "Breakpoint manipulation"
    },
    {
      "id": 100,
      "name": "Teens",
      "setId": 30,
      "theme": "Modern",
      "description": "Power-3 and name-based synergy"
    },
    {
      "id": 101,
      "name": "Adolescent Epic Geckos",
      "setId": 31,
      "theme": "80s Cartoons",
      "description": "Turtle parody faction"
    },
    {
      "id": 102,
      "name": "G.I. Gerald",
      "setId": 31,
      "theme": "80s Cartoons",
      "description": "G.I. Joe parody faction"
    },
    {
      "id": 103,
      "name": "Pearl and the Images",
      "setId": 31,
      "theme": "80s Cartoons",
      "description": "Gem parody faction"
    },
    {
      "id": 104,
      "name": "Rulers of the Cosmos",
      "setId": 31,
      "theme": "80s Cartoons",
      "description": "He-Man parody faction"
    },
    {
      "id": 105,
      "name": "Slashers",
      "setId": 32,
      "theme": "Famous Movie Slashers",
      "description": "Destruction, power counters, recursion, unique weapons."
    },
   {
      "id": 106,
      "name": "Clowns",
      "setId": 33,
      "theme": "Clowns",
      "description": "Replay standard actions from discard for power boost"
    }
  ]
};

// Simple pseudo-random number generator for consistent shuffling (optional, but good for reproducibility)
const RandoM = {
  seed: Date.now(), // Initial seed set dynamically
  setSeed: function(s) {
    this.seed = s;
  },
  random: function() {
    const x = Math.sin(this.seed++) * 10000;
    return x - Math.floor(x);
  }
};


const USER_PREFS_STORAGE_KEY = 'smashUpUserSelections';
const USER_PREFS_VERSION = 4; // Increment this for new preference structure, as useCustomData is now part of it
const SMASH_UP_GAME_DATA_STORAGE_KEY = 'smashUpGameData'; // New constant for game data storage

const App = () => {
  // State to manage player names, defaulting to 4 players
  const [players, setPlayers] = useState(
    Array.from({ length: 4 }, (_, i) => ({
      id: i,
      name: ``,
      factions: [], // Factions kept by this player
      initialDraftFactions: [], // Factions initially dealt for 'discard_1_of_3' mode
    }))
  );

  // State to hold the fetched game data
  const [smashUpData, setSmashUpData] = useState({ sets: [], factions: [] });
  const [loadingData, setLoadingData] = useState(true); // Corrected syntax
  const [dataError, setDataError] = useState(null);

  // State for selected factions: { setId: Set<factionId> }
  // This allows toggling individual factions within a set.
  // Initialize as empty, then populate once data is loaded.
  const [selectedFactionsBySet, setSelectedFactionsBySet] = useState({});

  // State to manage the selected draft mode
  const [draftMode, setDraftMode] = useState('snake_draft'); // Renamed from 'pick_1_of_2_snake' to 'snake_draft'

  // NEW STATE: Number of factions to present for 'pick_2_of_X' mode
  const [numFactionsToPresent, setNumFactionsToPresent] = useState(6); // Default to 6 (pick 2 of 6)

  // NEW STATE: How to handle unchosen factions in 'pick_2_of_X' mode
  const [unchosenFactionsOption, setUnchosenFactionsOption] = useState('remove_from_play'); // 'remove_from_play' or 're_integrate'

  // NEW STATE: Number of factions to present for 'snake_draft' mode (formerly pick_1_of_2_snake)
  const [snakeDraftNumFactionsToPresent, setSnakeDraftNumFactionsToPresent] = useState(2); // Default to 2

  // NEW STATE: How to handle unchosen factions in 'snake_draft' mode
  const [snakeDraftUnchosenFactionsOption, setSnakeDraftUnchosenFactionsOption] = useState('remove_from_play'); // Default to 'remove_from_play'

  // NEW STATE: Filter text for Snake Draft 'All' mode
  const [filterText, setFilterText] = useState('');

  // NEW STATE: For "Random Subset" option in Snake Draft
  const [useRandomSubsetForSnakeDraft, setUseRandomSubsetForSnakeDraft] = useState(false);
  const [randomSubsetSize, setRandomSubsetSize] = useState(0);

  // NEW STATE: For Official : Custom ratio in Snake Draft
  const [useOfficialCustomRatio, setUseOfficialCustomRatio] = useState(false);
  const [officialCustomRatio, setOfficialCustomRatio] = useState('1:1'); // Default ratio

  // NEW STATE: To control whether custom data is loaded
  const [useCustomData, setUseCustomData] = useState(false); // Default to false


  // State to track which faction button is currently hovered over in the discard modal
  const [hoveredFactionId, setHoveredFactionId] = useState(null);

  // State to track if the draft is in progress
  const [draftInProgress, setDraftInProgress] = useState(false);
  // State to store the history of draft picks (or discards) for DISPLAY
  const [displayDraftHistory, setDisplayDraftHistory] = useState([]);
  // State to store snapshots of the entire relevant draft state for UNDO functionality
  const [draftStateSnapshots, setDraftStateSnapshots] = useState([]);

  // State to control the visibility of the draft results modal
  const [showDraftResultsModal, setShowDraftResultsModal] = useState(false);
  // State for displaying messages to the user (e.e.g., warnings)
  const [message, setMessage] = useState('');
  // Removed userFeedbackMessage state as it's no longer needed for explicit save/load buttons

  // State for the factions presented for choice during a pick (2 for snake, 3 for discard, X for pick 2 of X)
  const [draftChoices, setDraftChoices] = useState(null); // null or [faction1, faction2] or [faction1, faction2, faction3] or [faction1, ..., factionX]

  // State to track the current player index (0-based) whose turn it is
  const [currentPlayerTurnIndex, setCurrentPlayerTurnIndex] = useState(0);

  // State to track the overall pick number (1-based)
  const [currentOverallPick, setCurrentOverallPick] = useState(0);

  // Ref to hold the current draft pool (shuffled and shrinking)
  const currentDraftPool = useRef([]);

  // Ref to hold the randomized order of players for 'discard_1_of_3' and 'pick_2_of_X' modes
  const playerTurnOrder = useRef([]);

  // State to manage the expanded/collapsed state of each accordion panel
  // FIX: Corrected useState initialization for expandedSets
  const [expandedSets, setExpandedSets] = useState(new Set()); // Stores set IDs that are currently expanded

  // Define sets and factions to be excluded from default selection
  // Modify these arrays to change what is unchecked by default.
  const defaultExcludedSetNames = useMemo(() => ["Smash Up: Munchkin"], []);
  const defaultExcludedFactionNames = useMemo(() => ["Geeks", "Zombies"], []);

  // NEW STATE: To store the results of the last completed draft for re-opening the modal
  const [lastCompletedDraft, setLastCompletedDraft] = useState(null);

  // State to manage selected factions for 'pick_2_of_X' mode
  const [selectedPick2OfXFactions, setSelectedPick2OfXFactions] = useState([]);

  // Memoized list of factions truly available for drafting based on selectedFactionsBySet and fetched data
  const availableFactionsForDraft = useMemo(() => {
    let factions = [];
    if (!smashUpData.factions) return []; // Guard against data not loaded yet
    for (const setId in selectedFactionsBySet) {
      const selectedFactionIdsInSet = selectedFactionsBySet[setId];
      // Filter factions only if they are in the selectedFactionIdsInSet Set
      const factionsInThisSet = smashUpData.factions.filter(f =>
        f.setId === parseInt(setId) && selectedFactionIdsInSet.has(f.id) // Convert setId to int for comparison
      );
      factions.push(...factionsInThisSet);
    }
    return factions;
  }, [selectedFactionsBySet, smashUpData.factions]); // Removed availableFactionsForDraft.length from dependencies

  // Memoized count of ALL active factions (for "Factions excluded" calculation)
  const allActiveFactionsCount = useMemo(() => {
    if (!smashUpData.factions || !smashUpData.sets) return 0;
    const activeSetIds = new Set(
      smashUpData.sets.filter(set => set.factionCount > 0 && set.status === 'active').map(set => set.id)
    );
    return smashUpData.factions.filter(faction => activeSetIds.has(faction.setId)).length;
  }, [smashUpData.factions, smashUpData.sets]);

  // Memoized list of *individual* excluded factions (for the full tooltip)
  const excludedFactions = useMemo(() => {
    if (!smashUpData.factions || !smashUpData.sets) return [];

    const selectedFactionIds = new Set();
    for (const setId in selectedFactionsBySet) {
        selectedFactionsBySet[setId].forEach(factionId => selectedFactionIds.add(factionId));
    }

    const activeSetIds = new Set(
        smashUpData.sets.filter(set => set.factionCount > 0 && set.status === 'active').map(set => set.id)
    );

    const allActiveFactions = smashUpData.factions.filter(faction => activeSetIds.has(faction.setId));

    const currentlyExcluded = allActiveFactions.filter(
        faction => !selectedFactionIds.has(faction.id)
    );

    return currentlyExcluded.sort((a, b) => a.name.localeCompare(b.name));
  }, [smashUpData.factions, smashUpData.sets, selectedFactionsBySet]);

  // Memoized list for the summarized direct display of excluded factions, with type for color-coding
  const excludedSummaryList = useMemo(() => {
    if (!smashUpData.factions || !smashUpData.sets || excludedFactions.length === 0) return [];

    const summaryParts = [];
    const processedSetIds = new Set(); // To track sets already summarized

    // Create a temporary set for quick lookup of excluded faction IDs
    const excludedFactionIdsSet = new Set(excludedFactions.map(f => f.id));

    // First, identify and add entirely excluded sets
    smashUpData.sets
      .filter(set => set.factionCount > 0 && set.status === 'active')
      .forEach(set => {
        const factionsInSet = smashUpData.factions.filter(f => f.setId === set.id);
        const isEntireSetExcluded = factionsInSet.length > 0 &&
                                    factionsInSet.every(f => excludedFactionIdsSet.has(f.id));

        if (isEntireSetExcluded) {
          summaryParts.push({ name: set.name, type: 'set' });
          processedSetIds.add(set.id);
        }
      });

    // Then, add individual excluded factions that are not part of a fully excluded set
    // Ensure we only add factions that are genuinely excluded and not covered by a summarized set
    excludedFactions.forEach(faction => {
      if (!processedSetIds.has(faction.setId)) { // Only add if its set was not fully excluded
        summaryParts.push({ name: faction.name, type: 'faction' });
      }
    });

    return summaryParts.sort((a, b) => a.name.localeCompare(b.name)); // Sort the combined list by name
  }, [smashUpData.factions, smashUpData.sets, excludedFactions]);

  // Deselect all sets and their factions
  const handleSelectNoneSetsAndFactions = useCallback(() => {
    setSelectedFactionsBySet({}); // Clear the entire selection map
  }, []); // Empty dependency array as it doesn't depend on any state/props

  // Function to select ALL sets and their factions (used by the "Select All" button)
  const handleSelectAllSetsAndFactions = useCallback((sets, factions) => {
    const newSelection = {};
    sets
      .filter(set => set.factionCount > 0 && set.status === 'active')
      .forEach(set => {
        const factionsInSet = factions.filter(f => f.setId === set.id);
        if (factionsInSet.length > 0) {
          newSelection[set.id] = new Set(factionsInSet.map(f => f.id));
        }
      });
    setSelectedFactionsBySet(newSelection);
  }, []);

  // Function to initialize selection based on default exclusions or loaded preferences
  // This function will now ONLY set faction selections, not other preferences.
  const initializeSelection = useCallback((sets, factions, loadedFactionPreferences = null, includeCustomFactionsFlag) => {
    let newSelection = {};

    if (loadedFactionPreferences && loadedFactionPreferences.selectedFactionsBySet) { // Check for selectedFactionsBySet existence
        for (const setId in loadedFactionPreferences.selectedFactionsBySet) {
            newSelection[setId] = new Set(loadedFactionPreferences.selectedFactionsBySet[setId]);
        }
        console.log("initializeSelection: Using loaded faction preferences.");
    } else {
        console.log("initializeSelection: Applying default exclusions.");
        const excludedSetIds = new Set(sets.filter(set => defaultExcludedSetNames.includes(set.name)).map(set => set.id));
        const excludedFactionIds = new Set(factions.filter(faction => defaultExcludedFactionNames.includes(faction.name)).map(faction => faction.id));

        sets.filter(set => set.factionCount > 0 && set.status === 'active')
            .forEach(set => {
                if (excludedSetIds.has(set.id)) {
                    return;
                }
                const factionsInSet = factions.filter(f => f.setId === set.id);
                const selectedFactionsInSet = new Set();
                factionsInSet.forEach(faction => {
                    if (!excludedFactionIds.has(faction.id)) {
                        selectedFactionsInSet.add(faction.id);
                    }
                });
                if (selectedFactionsInSet.size > 0) {
                    newSelection[set.id] = selectedFactionsInSet;
                }
            });
    }

    // Only add custom factions if the flag passed to this function is TRUE
    if (includeCustomFactionsFlag) {
        console.log("initializeSelection: Adding custom factions because includeCustomFactionsFlag is true.");
        factions.filter(f => f.isCustom).forEach(customFaction => {
            if (!newSelection[customFaction.setId]) {
                newSelection[customFaction.setId] = new Set();
            }
            newSelection[customFaction.setId].add(customFaction.id);
        });
    }

    setSelectedFactionsBySet(newSelection);
}, [defaultExcludedSetNames, defaultExcludedFactionNames]);

  // Function to clear saved selection from localStorage (now only called by resetApp)
  const clearSavedSelection = useCallback(() => {
    try {
      localStorage.removeItem(USER_PREFS_STORAGE_KEY);
      console.log("Cleared saved selection from localStorage.");
    } catch (error) {
      console.error("Failed to clear saved preferences from localStorage:", error);
    }
  }, []);

  // Function to save current selection to localStorage (now only called by startDraft and toggle)
  const saveCurrentSelection = useCallback(() => {
    const serializableSelection = {};
    for (const setId in selectedFactionsBySet) {
      serializableSelection[setId] = Array.from(selectedFactionsBySet[setId]); // Convert Set to Array for serialization
    }

    // Prepare player data for saving (only id and name needed)
    const serializablePlayers = players.map(player => ({
      id: player.id,
      name: player.name,
    }));

    const prefsToSave = {
      version: USER_PREFS_VERSION,
      selectedFactionsBySet: serializableSelection,
      players: serializablePlayers, // Save player names and count
      draftMode: draftMode, // Save current draft mode
      numFactionsToPresent: numFactionsToPresent, // Save pick 2 of X option
      unchosenFactionsOption: unchosenFactionsOption, // Save pick 2 of X option
      snakeDraftNumFactionsToPresent: snakeDraftNumFactionsToPresent, // Save snake draft option
      snakeDraftUnchosenFactionsOption: snakeDraftUnchosenFactionsOption, // Save snake draft option
      useRandomSubsetForSnakeDraft: useRandomSubsetForSnakeDraft, // Save new option
      randomSubsetSize: randomSubsetSize, // Save new option
      useOfficialCustomRatio: useOfficialCustomRatio, // Save new option
      officialCustomRatio: officialCustomRatio, // Save new option
      useCustomData: useCustomData, // NEW: Save useCustomData state
    };
    try {
      localStorage.setItem(USER_PREFS_STORAGE_KEY, JSON.stringify(prefsToSave));
      console.log("Saved current selection and player data to localStorage.");
    }
    catch (error) {
      console.error("Failed to save preferences to localStorage:", error);
    }
  }, [selectedFactionsBySet, players, draftMode, numFactionsToPresent, unchosenFactionsOption, snakeDraftNumFactionsToPresent, snakeDraftUnchosenFactionsOption, useRandomSubsetForSnakeDraft, randomSubsetSize, useOfficialCustomRatio, officialCustomRatio, useCustomData]); // Added useCustomData to dependencies


  // NEW: Function to load all game data (official + optional custom)
  // This function now directly calls initializeSelection after data is set.
  const fetchGameData = useCallback(async (includeCustomFlag) => {
    setLoadingData(true);
    setDataError(null);
    let currentData = DEFAULT_SMASH_UP_DATA;

    // Attempt to load saved game data from localStorage first
    const cachedGameData = localStorage.getItem(SMASH_UP_GAME_DATA_STORAGE_KEY);
    if (cachedGameData) {
      try {
        const parsedCachedData = JSON.parse(cachedGameData);
        // Check if cached data is reasonably recent or has a version
        if (parsedCachedData && parsedCachedData.version >= DEFAULT_SMASH_UP_DATA.version) {
          currentData = parsedCachedData;
          console.log("Using cached game data. Version:", currentData.version);
        }
      } catch (e) {
        console.warn("Failed to parse cached game data, fetching fresh.", e);
        localStorage.removeItem(SMASH_UP_GAME_DATA_STORAGE_KEY); // Clear bad cache
      }
    }

    // Always try to fetch latest official data, even if cached data exists
    try {
      const response = await fetch('https://raw.githubusercontent.com/Liukus/smashuprandomizer/refs/heads/main/smashUpData.json');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const remoteOfficialData = await response.json();
      // Only update if remote data is newer or different
      if (remoteOfficialData.version > currentData.version) {
        currentData = remoteOfficialData;
        localStorage.setItem(SMASH_UP_GAME_DATA_STORAGE_KEY, JSON.stringify(currentData));
        console.log("Fetched latest official data. Version:", currentData.version);
      } else {
        console.log("Cached official data is up-to-date or newer than remote.");
      }
    } catch (error) {
      console.error("Failed to fetch latest Smash Up data from GitHub, using current (cached or bundled) default:", error);
      setDataError("Failed to load latest game data from GitHub. Using local data.");
    }

    // If includeCustomFlag is true, try to fetch and merge custom data
    if (includeCustomFlag) {
        try {
            const customResponse = await fetch('https://raw.githubusercontent.com/Liukus/smashuprandomizer/refs/heads/customdata/customSmashUpData.json');
            if (!customResponse.ok) {
                throw new Error(`HTTP error! status: ${customResponse.status}`);
            }
            const customData = await customResponse.json();

            if (!customData || typeof customData !== 'object' || !Array.isArray(customData.sets) || !Array.isArray(customData.factions)) {
                throw new Error("Invalid custom data format. Expected an object with 'sets' and 'factions' arrays.");
            }

            // Merge custom data into the currentData (which is now either default or latest official)
            const existingSetsMap = new Map(currentData.sets.map(s => [s.id, s]));
            customData.sets.forEach(customSet => {
                existingSetsMap.set(customSet.id, customSet);
            });
            const mergedSets = Array.from(existingSetsMap.values()).sort((a, b) => a.id - b.id);

            const existingFactionsMap = new Map(currentData.factions.map(f => [f.id, f]));
            customData.factions.forEach(customFaction => {
                existingFactionsMap.set(customFaction.id, { ...customFaction, isCustom: true });
            });
            const mergedFactions = Array.from(existingFactionsMap.values()).sort((a, b) => a.name.localeCompare(b.name));

            currentData = {
                ...currentData,
                sets: mergedSets,
                factions: mergedFactions,
                // Apply roundToTwoDecimals to ensure correct precision for version number
                version: Math.max(currentData.version || 0, customData.version || 0, (currentData.version || 0) + 0.01) // Simplified version update, assuming version is a number
            };
            setMessage('Custom data loaded successfully!');
        } catch (error) {
            console.error("Error fetching or merging custom data:", error);
            setMessage(`Failed to load custom data: ${error.message}. Reverting to official data only.`);
            // If custom data fails, ensure useCustomData state is set to false
            setUseCustomData(false); // This will trigger re-fetchGameData with false
        }
    }

    setSmashUpData(currentData); // This is the *final* data for the render cycle

    // Now, after smashUpData is set, initialize the selection based on this data
    // and the loaded user preferences.
    const savedPrefs = localStorage.getItem(USER_PREFS_STORAGE_KEY);
    let loadedFactionPreferences = null;
    if (savedPrefs) {
      try {
        const parsedPrefs = JSON.parse(savedPrefs);
        if (parsedPrefs.version === USER_PREFS_VERSION) {
          loadedFactionPreferences = parsedPrefs;
        }
      } catch (error) {
        console.error("Failed to parse saved user preferences for faction selection during data fetch:", error);
      }
    }
    initializeSelection(currentData.sets, currentData.factions, loadedFactionPreferences, includeCustomFlag);

    setLoadingData(false);
    console.log("Final smashUpData state updated. Version:", currentData.version);
  }, [initializeSelection]);

  // Effect to load preferences (including useCustomData) on initial mount
  useEffect(() => {
    // Initialize RandoM seed on component mount
    RandoM.setSeed(Date.now());

    const loadPreferencesAndData = async () => {
      const savedPrefs = localStorage.getItem(USER_PREFS_STORAGE_KEY);
      let initialUseCustomData = false;
      if (savedPrefs) {
        try {
          const parsedPrefs = JSON.parse(savedPrefs);
          if (parsedPrefs.version === USER_PREFS_VERSION) {
            initialUseCustomData = parsedPrefs.useCustomData || false;
            setPlayers(parsedPrefs.players.map(p => ({ ...p, factions: [], initialDraftFactions: [] })));
            setDraftMode(parsedPrefs.draftMode || 'snake_draft');
            setNumFactionsToPresent(parsedPrefs.numFactionsToPresent || 6);
            setUnchosenFactionsOption(parsedPrefs.unchosenFactionsOption || 'remove_from_play');
            setSnakeDraftNumFactionsToPresent(parsedPrefs.snakeDraftNumFactionsToPresent || 2);
            setSnakeDraftUnchosenFactionsOption(parsedPrefs.snakeDraftUnchosenFactionsOption || 'remove_from_play');
            setUseRandomSubsetForSnakeDraft(parsedPrefs.useRandomSubsetForSnakeDraft || false);
            setRandomSubsetSize(parsedPrefs.randomSubsetSize || 0);
            setUseOfficialCustomRatio(parsedPrefs.useOfficialCustomRatio || false);
            setOfficialCustomRatio(parsedPrefs.officialCustomRatio || '1:1');
            console.log("Loaded all user preferences from localStorage.");
          } else {
            console.warn("Saved preferences version mismatch, resetting to defaults.");
            clearSavedSelection(); // Clear old version preferences
          }
        } catch (error) {
          console.error("Failed to parse saved user preferences, resetting to defaults:", error);
          clearSavedSelection(); // Clear corrupted data
        }
      }
      setUseCustomData(initialUseCustomData); // Set this state based on loaded preferences
      // The subsequent fetchGameData will be triggered by the useCustomData dependency.
    };
    loadPreferencesAndData();
  }, [clearSavedSelection]);

  // This useEffect remains, and is crucial for when useCustomData *changes* (either from initial load or user toggle)
  useEffect(() => {
    fetchGameData(useCustomData);
  }, [fetchGameData, useCustomData]);


  // Memoized check if custom factions are currently loaded
  const hasCustomFactionsLoaded = useMemo(() => {
    return smashUpData.factions.some(f => f.isCustom);
  }, [smashUpData.factions]);


  // Function to handle closing the modal and resetting draft state if in progress
  const handleCloseModal = () => {
    if (draftInProgress) {
      setDraftInProgress(false);
      setMessage('Draft cancelled.');
      setDraftChoices(null); // Clear any active choices
      setCurrentOverallPick(0); // Reset pick counter
      setCurrentPlayerTurnIndex(0); // Reset player turn
      setDraftStateSnapshots([]); // Clear snapshots on cancel
      setDisplayDraftHistory([]); // Clear display history on cancel
    }
    setShowDraftResultsModal(false);
    setFilterText('');
  };

  // Function to re-open the last completed draft results modal
  const handleReopenLastDraft = () => {
    if (lastCompletedDraft) {
      setPlayers(lastCompletedDraft.players);
      setDisplayDraftHistory(lastCompletedDraft.history); // Use displayDraftHistory
      setDraftMode(lastCompletedDraft.mode);
      setNumFactionsToPresent(lastCompletedDraft.numFactionsToPresent);
      setUnchosenFactionsOption(lastCompletedDraft.unchosenFactionsOption);
      setSnakeDraftNumFactionsToPresent(lastCompletedDraft.snakeDraftNumFactionsToPresent);
      setSnakeDraftUnchosenFactionsOption(lastCompletedDraft.snakeDraftUnchosenFactionsOption);
      setUseRandomSubsetForSnakeDraft(lastCompletedDraft.useRandomSubsetForSnakeDraft || false); // Load new option
      setRandomSubsetSize(lastCompletedDraft.randomSubsetSize || 0); // Load new option
      setUseOfficialCustomRatio(lastCompletedDraft.useOfficialCustomRatio || false); // Load new option
      setOfficialCustomRatio(lastCompletedDraft.officialCustomRatio || '1:1'); // Load new option
      setUseCustomData(lastCompletedDraft.useCustomData || false); // Load new option
      setDraftInProgress(false); // It's a completed draft being viewed
      setCurrentOverallPick(players.length * 2); // Set to max pick for completed view
      setCurrentPlayerTurnIndex(0); // Reset for display purposes
      setMessage('Viewing last completed draft results.');
      setDraftChoices(null); // No active choices when viewing results
      setDraftStateSnapshots([]); // Clear snapshots as we're viewing a completed draft
      setShowDraftResultsModal(true); // Open the modal
    }
  };

  // Helper function to deep copy state for snapshots
  const deepCopyState = useCallback((playersData, draftPoolData, currentPlayerIdx, overallP, choices, history) => {
    return {
      players: JSON.parse(JSON.stringify(playersData)),
      draftPool: [...draftPoolData],
      currentPlayerIndex: currentPlayerIdx,
      overallPick: overallP,
      draftChoices: choices ? JSON.parse(JSON.stringify(choices)) : null,
      displayHistory: [...history],
    };
  }, []);


  // Start the draft
  const startDraft = () => {
    setMessage(''); // Clear any previous messages
    RandoM.setSeed(Date.now()); // Reset seed for a new, unique draft

    // Save current configuration to local storage when draft begins
    saveCurrentSelection();

    const numPlayers = players.length; // This variable is used below, so it's not unused.
    const totalPicksNeeded = numPlayers * 2; // Each player picks 2 factions
    let initialDraftPool = []; // Start with an empty pool, will be populated based on options
    let requiredFactionsForPoolCheck = totalPicksNeeded; // Default minimum factions needed in the pool

    if (draftMode === 'snake_draft') {
        const allOfficialFactions = availableFactionsForDraft.filter(f => !f.isCustom);
        const allCustomFactions = availableFactionsForDraft.filter(f => f.isCustom);

        if (useRandomSubsetForSnakeDraft && useOfficialCustomRatio) {
            // Combined mode: Random Subset with Official:Custom Ratio
            const ratioParts = officialCustomRatio.split(':').map(Number);
            if (ratioParts.length !== 2 || isNaN(ratioParts[0]) || isNaN(ratioParts[1]) || ratioParts[0] < 0 || ratioParts[1] < 0 || (ratioParts[0] === 0 && ratioParts[1] === 0)) {
                setMessage("Invalid Official : Custom ratio. Please enter two non-negative integers separated by a colon (e.g., '3:5').");
                return;
            }
            const [officialRatioVal, customRatioVal] = ratioParts;
            const ratioSum = officialRatioVal + customRatioVal;

            let numOfficialInSubset = Math.round(randomSubsetSize * (officialRatioVal / ratioSum));
            let numCustomInSubset = randomSubsetSize - numOfficialInSubset;

            // Ensure we don't try to get more factions than available
            if (numOfficialInSubset > allOfficialFactions.length) {
                numOfficialInSubset = allOfficialFactions.length;
                numCustomInSubset = randomSubsetSize - numOfficialInSubset; // Adjust custom based on actual official count
            }
            if (numCustomInSubset > allCustomFactions.length) {
                numCustomInSubset = allCustomFactions.length;
                numOfficialInSubset = randomSubsetSize - numCustomInSubset; // Adjust official based on actual custom count
            }

            // Final check on whether the desired subset size and ratio can be met
            if (numOfficialInSubset + numCustomInSubset < randomSubsetSize) {
                setMessage(`Not enough factions to create a subset of size ${randomSubsetSize} with ratio ${officialCustomRatio}. Available: Official ${allOfficialFactions.length}, Custom ${allCustomFactions.length}. Needed (approx): Official ${Math.round(randomSubsetSize * (officialRatioVal / ratioSum))}, Custom ${randomSubsetSize - Math.round(randomSubsetSize * (officialRatioVal / ratioSum))}.`);
                return;
            }

            const selectedOfficial = shuffleArray([...allOfficialFactions]).slice(0, numOfficialInSubset);
            const selectedCustom = shuffleArray([...allCustomFactions]).slice(0, numCustomInSubset);
            initialDraftPool = shuffleArray([...selectedOfficial, ...selectedCustom]); // Combine and re-shuffle
            requiredFactionsForPoolCheck = totalPicksNeeded; // Still need enough for players to pick their 2 factions
        } else if (useRandomSubsetForSnakeDraft) {
            // Only Random Subset enabled
            if (!Number.isInteger(randomSubsetSize) || randomSubsetSize < totalPicksNeeded || randomSubsetSize > availableFactionsForDraft.length) {
                setMessage(
                    `For Random Subset: Desired Subset Size must be an integer between ${totalPicksNeeded} and ${availableFactionsForDraft.length}.`
                );
                return;
            }
            initialDraftPool = shuffleArray([...availableFactionsForDraft]).slice(0, randomSubsetSize);
            requiredFactionsForPoolCheck = totalPicksNeeded;
        } else if (useOfficialCustomRatio) { // This block will now only be reached if useRandomSubsetForSnakeDraft is false
            // Only Official : Custom Ratio enabled (no random subset)
            // This case should ideally not be reachable due to UI logic, but adding a check for robustness
            if (!smashUpData.factions.some(f => f.isCustom)) {
                setMessage("Cannot use Official : Custom Ratio without custom factions. Please import custom data or uncheck the option.");
                return;
            }

            const ratioParts = officialCustomRatio.split(':').map(Number);
            if (ratioParts.length !== 2 || isNaN(ratioParts[0]) || isNaN(ratioParts[1]) || ratioParts[0] < 0 || ratioParts[1] < 0 || (ratioParts[0] === 0 && ratioParts[1] === 0)) {
                setMessage("Invalid Official : Custom ratio. Please enter two non-negative integers separated by a colon (e.g., '3:5').");
                return;
            }
            const [officialRatioVal, customRatioVal] = ratioParts;
            const ratioSum = officialRatioVal + customRatioVal;

            let numOfficialToSelect = Math.round(totalPicksNeeded * (officialRatioVal / ratioSum));
            let numCustomToSelect = totalPicksNeeded - numOfficialToSelect;

            if (numOfficialToSelect > allOfficialFactions.length) {
                numOfficialToSelect = allOfficialFactions.length;
                numCustomToSelect = totalPicksNeeded - numOfficialToSelect;
            }
            if (numCustomToSelect > allCustomFactions.length) {
                numCustomToSelect = allCustomFactions.length;
                numOfficialToSelect = totalPicksNeeded - numCustomToSelect;
            }

            if (numOfficialToSelect + numCustomToSelect < totalPicksNeeded) {
                setMessage(`Not enough factions to meet the required ${totalPicksNeeded} picks with the specified ratio (${officialCustomRatio}). Available: Official ${allOfficialFactions.length}, Custom ${allCustomFactions.length}. Needed: Official ${Math.round(totalPicksNeeded * (officialRatioVal / ratioSum))}, Custom ${totalPicksNeeded - Math.round(totalPicksNeeded * (officialRatioVal / ratioSum))}.`);
                return;
            }
            
            const selectedOfficial = shuffleArray([...allOfficialFactions]).slice(0, numOfficialToSelect);
            const selectedCustom = shuffleArray([...allCustomFactions]).slice(0, numCustomToSelect);
            initialDraftPool = shuffleArray([...selectedOfficial, ...selectedCustom]);
            requiredFactionsForPoolCheck = totalPicksNeeded;
        } else {
            // Standard Snake Draft (no subset, no ratio)
            initialDraftPool = [...availableFactionsForDraft];
            if (snakeDraftNumFactionsToPresent !== 'all' && snakeDraftUnchosenFactionsOption === 'remove_from_play') {
                requiredFactionsForPoolCheck = numPlayers * 2 * snakeDraftNumFactionsToPresent;
            } else if (snakeDraftNumFactionsToPresent !== 'all' && snakeDraftUnchosenFactionsOption === 're_integrate') {
                requiredFactionsForPoolCheck = (numPlayers * 2) + (snakeDraftNumFactionsToPresent - 1);
            } else if (snakeDraftNumFactionsToPresent === 'all') { // When 'all' is selected, it implicitly re-integrates
                requiredFactionsForPoolCheck = numPlayers * 2;
            }
        }
    } else if (draftMode === 'discard_1_of_3') {
        initialDraftPool = [...availableFactionsForDraft];
        requiredFactionsForPoolCheck = numPlayers * 3;
    } else if (draftMode === 'pure_random_assignment') {
        initialDraftPool = [...availableFactionsForDraft];
        requiredFactionsForPoolCheck = numPlayers * 2;
    } else if (draftMode === 'pick_2_of_X') {
        initialDraftPool = [...availableFactionsForDraft];
        if (unchosenFactionsOption === 'remove_from_play') {
            requiredFactionsForPoolCheck = numPlayers * numFactionsToPresent;
        } else { // 're_integrate'
            requiredFactionsForPoolCheck = (numPlayers * 2) + (numFactionsToPresent - 2);
        }
    }

    // Final validation for general pool size if not handled by ratio/subset logic
    if (initialDraftPool.length < requiredFactionsForPoolCheck) {
      setMessage(
        `Not enough unique factions (${initialDraftPool.length}) for the selected draft mode. You need at least ${requiredFactionsForPoolCheck} factions. Please select more sets or factions.`
      );
      return;
    }

    // Reset draft state for players and history
    const initialResetPlayers = players.map(p => ({ ...p, factions: [], initialDraftFactions: [] }));
    setPlayers(initialResetPlayers); // Set the state
    setDisplayDraftHistory([]); // Clear display history at the start of a new draft
    setDraftStateSnapshots([]); // Clear snapshots initially, they'll be added by the first turn setup
    currentDraftPool.current = shuffleArray(initialDraftPool); // Initialize pool with the determined initial pool
    playerTurnOrder.current = shuffleArray([...initialResetPlayers.map(p => p.id)]); // Randomize player order for turn-based modes
    
    setDraftInProgress(true);
    setShowDraftResultsModal(true);
    setFilterText('');
    

    if (draftMode === 'pure_random_assignment') {
      performPureRandomAssignment(initialResetPlayers); // Pass the reset players
    } else if (draftMode === 'discard_1_of_3') {
      performInitialDiscardAssignment(initialResetPlayers); // Pass the reset players
    } else if (draftMode === 'pick_2_of_X') {
        startPick2OfXRound(0, initialResetPlayers); // Start the first player's turn for pick 2 of X
    }
    else { // 'snake_draft'
      presentFactionChoices(1, initialResetPlayers); // Pass the reset players
    }
  };

  // Logic for Pure Random Assignment draft mode
  const performPureRandomAssignment = (currentPlayersState) => {
    // For pure random, undo means resetting the whole app, so no internal snapshots needed.
    const numPlayers = currentPlayersState.length;
    let shuffledFactions = [...currentDraftPool.current]; // Use currentDraftPool for random assignments
    let newPlayers = currentPlayersState.map(p => ({ ...p, factions: [] })); // Start with cleared factions
    let newDisplayDraftHistory = [];
    let overallPickCounter = 1;

    for (let i = 0; i < numPlayers; i++) {
      const player = newPlayers[i];
      // Assign 2 factions to each player
      for (let j = 0; j < 2; j++) {
        if (shuffledFactions.length > 0) {
          const assignedFaction = shuffledFactions.shift(); // Take from the beginning of shuffled array
          player.factions.push(assignedFaction);
          newDisplayDraftHistory.push({
            player: getPlayerDisplayName(player), // Use display name for history
            faction: assignedFaction.name,
            type: 'assigned',
            round: j + 1, // Simplified round for random assignment
            pickInRound: 1, // Always 1 for this mode
            overallPick: overallPickCounter++,
          });
        }
      }
    }
    setPlayers(newPlayers);
    setDisplayDraftHistory(newDisplayDraftHistory); // Update display history
    setDraftInProgress(false); // Draft is immediately complete
    setMessage('Pure Random Assignment Draft complete!');
    // Clear draft choices as no interaction is needed
    setDraftChoices(null);
    setDraftStateSnapshots([]); // Clear snapshots as it's a completed, non-undoable draft

    // Store completed draft results
    setLastCompletedDraft({
      players: newPlayers,
      history: newDisplayDraftHistory, // Use the display history
      mode: 'pure_random_assignment',
      numFactionsToPresent: numFactionsToPresent,
      unchosenFactionsOption: unchosenFactionsOption,
      snakeDraftNumFactionsToPresent: snakeDraftNumFactionsToPresent,
      snakeDraftUnchosenFactionsOption: snakeDraftUnchosenFactionsOption,
      useRandomSubsetForSnakeDraft: useRandomSubsetForSnakeDraft, // Save new option
      randomSubsetSize: randomSubsetSize, // Save new option
      useOfficialCustomRatio: useOfficialCustomRatio, // Save new option
      officialCustomRatio: officialCustomRatio, // Save new option
      useCustomData: useCustomData, // Save new option
    });
  };

  // Logic for initial faction assignment for 'discard_1_of_3' mode
  const performInitialDiscardAssignment = (currentPlayersState) => {
    // Start with the passed currentPlayersState which should already be reset
    let newPlayersState = currentPlayersState.map(p => ({ ...p, factions: [], initialDraftFactions: [] }));
    let shuffledFactions = [...currentDraftPool.current];

    // Deal 3 factions to each player in the newPlayersState array
    for (let i = 0; i < newPlayersState.length; i++) {
      for (let j = 0; j < 3; j++) {
        if (shuffledFactions.length > 0) {
          newPlayersState[i].initialDraftFactions.push(shuffledFactions.shift());
        }
      }
    }
    setPlayers(newPlayersState); // Update the players state with initialDraftFactions

    // Set up the first player's discard choice immediately using the newPlayersState data
    const firstPlayerId = playerTurnOrder.current[0];
    const firstPlayerIndex = newPlayersState.findIndex(p => p.id === firstPlayerId);

    if (firstPlayerIndex === -1) {
      console.error("Error: First player not found in initial setup for discard mode.");
      setDraftInProgress(false);
      setDraftChoices(null);
      setMessage("Error setting up discard phase.");
      return;
    }
    
    // Update state for the first turn
    setCurrentOverallPick(1); // Represents the 1st player to discard
    setCurrentPlayerTurnIndex(firstPlayerIndex);
    const choicesForFirstPlayer = newPlayersState[firstPlayerIndex].initialDraftFactions;
    setDraftChoices(choicesForFirstPlayer);

    // Take snapshot AFTER state is updated for the first turn
    // For the very first snapshot of a new draft, ensure history is empty
    setDraftStateSnapshots(prev => [...prev, deepCopyState(newPlayersState, currentDraftPool.current, firstPlayerIndex, 1, choicesForFirstPlayer, [])]);
  };

  // Function to handle the player's selected faction from the choices (for 'snake_draft' mode)
  const handleFactionChoice = (chosenFaction, currentPlayersState) => { // Accept currentPlayersState as argument
    const numPlayers = currentPlayersState.length;
    const pickNumberZeroBased = currentOverallPick - 1;

    // Recalculate current player index (could also use currentPlayerTurnIndex state)
    let playerIdx;
    const round = Math.floor(pickNumberZeroBased / numPlayers);
    if (round % 2 === 0) {
      playerIdx = pickNumberZeroBased % numPlayers;
    } else {
      playerIdx = numPlayers - 1 - (pickNumberZeroBased % numPlayers);
    }

    // Capture the number of factions in the pool *before* the current pick is removed
    const factionsInPoolBeforePick = currentDraftPool.current.length;

    // Update the chosen player's factions
    const updatedPlayers = currentPlayersState.map((p, index) =>
      index === playerIdx ? { ...p, factions: [...p.factions, chosenFaction] } : p
    );
    setPlayers(updatedPlayers);

    // Identify the unchosen factions
    const unchosenFactions = draftChoices.filter(f => f.id !== chosenFaction.id);
    const unchosenFactionNames = unchosenFactions.map(f => f.name);

    // Determine unchosenOption based on new logic
    // If random subset, custom ratio, or 'all' factions are presented, force re_integrate
    const unchosenOption = (useRandomSubsetForSnakeDraft || useOfficialCustomRatio || snakeDraftNumFactionsToPresent === 'all')
        ? 're_integrate'
        : snakeDraftUnchosenFactionsOption;

    // Update the main currentDraftPool based on the unchosenFactionsOption
    const currentNumFactionsToPresent = snakeDraftNumFactionsToPresent === 'all' ? factionsInPoolBeforePick : snakeDraftNumFactionsToPresent;

    if (unchosenOption === 'remove_from_play') {
        // Remove all presented factions from the pool
        currentDraftPool.current = currentDraftPool.current.filter(
            (faction) => !draftChoices.some(presented => presented.id === faction.id)
        );
    } else { // 're_integrate'
        // Only remove the chosen faction from the pool
        currentDraftPool.current = currentDraftPool.current.filter(
            (faction) => faction.id !== chosenFaction.id
        );
    }

    // Add this pick to the display draft history
    const historyEntry = {
        player: getPlayerDisplayName(currentPlayersState[playerIdx]),
        faction: chosenFaction.name,
        type: 'picked',
        round: round + 1,
        pickInRound: (pickNumberZeroBased % numPlayers) + 1,
        overallPick: currentOverallPick,
        numPresented: currentNumFactionsToPresent, // Use the calculated value here
    };

    if (unchosenOption === 'remove_from_play') { // Only add discarded names if they were actually removed
        historyEntry.discardedFactionNames = unchosenFactionNames;
    }

    // Create the new history array including the current entry
    const newDisplayDraftHistory = [...displayDraftHistory, historyEntry];
    setDisplayDraftHistory(newDisplayDraftHistory);

    setDraftChoices(null); // Clear the choices as the pick has been made
    setHoveredFactionId(null); // Clear hovered state after a choice is made
    setFilterText(''); // Clear filter text after a choice is made

    // Increment overall pick number for the next turn
    const nextOverallPick = currentOverallPick + 1;

    // Check if the draft is complete (all players have 2 factions)
    if (nextOverallPick > numPlayers * 2) {
      setDraftInProgress(false);
      setMessage('Draft complete!');
      // Store completed draft results
      setLastCompletedDraft({
        players: updatedPlayers,
        history: newDisplayDraftHistory, // Use the already constructed newDisplayDraftHistory
        mode: 'snake_draft',
        numFactionsToPresent: numFactionsToPresent, // These are global settings for the last draft
        unchosenFactionsOption: unchosenFactionsOption,
        snakeDraftNumFactionsToPresent: snakeDraftNumFactionsToPresent,
        snakeDraftUnchosenFactionsOption: snakeDraftUnchosenFactionsOption,
        useRandomSubsetForSnakeDraft: useRandomSubsetForSnakeDraft, // Save new option
        randomSubsetSize: randomSubsetSize, // Save new option
        useOfficialCustomRatio: useOfficialCustomRatio, // Save new option
        officialCustomRatio: officialCustomRatio, // Save new option
        useCustomData: useCustomData, // Save new option
      });
      setDraftStateSnapshots([]); // Clear snapshots once draft is completed and stored
    } else {
      // If not complete, prepare for the next pick
      presentFactionChoices(nextOverallPick, updatedPlayers); // Pass the *updated* players array
    }
  };

  // Function to present X faction choices to the current player (for 'snake_draft' mode)
  const presentFactionChoices = (pickNum, currentPlayersState) => { // Accept pickNum and currentPlayersState as arguments
    const numPlayers = currentPlayersState.length;
    const pickNumberZeroBased = pickNum - 1; // Use the passed pickNum for calculation

    // Determine current player based on snake draft logic
    let playerIdx;
    const round = Math.floor(pickNumberZeroBased / numPlayers);
    if (round % 2 === 0) { // Even rounds (0, 2, 4...): forward order
      playerIdx = pickNumberZeroBased % numPlayers;
    } else { // Odd rounds (1, 3, 5...): reverse order
      playerIdx = numPlayers - 1 - (pickNumberZeroBased % numPlayers);
    }
    
    // Set the state for the current turn
    setCurrentPlayerTurnIndex(playerIdx); // Update which player's turn it is
    setCurrentOverallPick(pickNum); // Set the current overall pick

    let drawnFactions = [];
    // If either random subset or official/custom ratio is active, the entire currentDraftPool is the set of choices
    if (useRandomSubsetForSnakeDraft || useOfficialCustomRatio) {
        drawnFactions = [...currentDraftPool.current];
        drawnFactions.sort((a, b) => a.name.localeCompare(b.name)); // Always sort alphabetically in this mode
    } else {
        const currentX = snakeDraftNumFactionsToPresent;
        // Check if there are enough factions left in the pool for X choices
        if (currentX === 'all') { // Handle 'all' case
            drawnFactions = [...currentDraftPool.current];
            drawnFactions.sort((a, b) => a.name.localeCompare(b.name)); // Always sort alphabetically
        } else if (currentDraftPool.current.length < currentX) {
            // If fewer than X factions are left, present all remaining.
            if (currentDraftPool.current.length > 0) {
                drawnFactions = [...currentDraftPool.current];
                setMessage(`Not enough factions left to present ${currentX} choices. Presenting all ${drawnFactions.length} remaining.`);
            } else {
                setMessage('No factions left to present choices. Draft ended early.');
                setDraftInProgress(false);
                setDraftChoices(null);
                return;
            }
        } else {
            const tempPool = [...currentDraftPool.current]; // Create a mutable copy
            for (let i = 0; i < currentX; i++) {
                const randomIndex = Math.floor(RandoM.random() * tempPool.length); // Use RandoM.random()
                drawnFactions.push(tempPool.splice(randomIndex, 1)[0]);
            }
        }
    }
    setDraftChoices(drawnFactions); // Set the choices to be displayed

    // Take snapshot AFTER state is updated for the current turn
    // If it's the very first pick (pickNum === 1), ensure history in snapshot is empty
    const historyForSnapshot = pickNum === 1 ? [] : displayDraftHistory;
    setDraftStateSnapshots(prev => [...prev, deepCopyState(currentPlayersState, currentDraftPool.current, playerIdx, pickNum, drawnFactions, historyForSnapshot)]);
  };


  // Function to handle the player discarding one faction (for 'discard_1_of_3' mode)
  const handleDiscardChoice = (discardedFaction) => {
    // Use a functional update for setPlayers to get the absolute latest state
    setPlayers(prevPlayers => {
      const currentPlayerId = playerTurnOrder.current[currentOverallPick - 1]; // Use playerTurnOrder
      const currentPlayerIndex = prevPlayers.findIndex(p => p.id === currentPlayerId);
      
      if (currentPlayerIndex === -1) {
        console.error("Error: Current player not found for discard choice in functional update.");
        setDraftInProgress(false); // Stop draft if critical error
        setDraftChoices(null);
        setMessage("Error processing discard. Please reset.");
        return prevPlayers; // Return original state to avoid further issues
      }

      const currentPlayer = prevPlayers[currentPlayerIndex];
      const keptFactions = currentPlayer.initialDraftFactions.filter(
        (faction) => faction.id !== discardedFaction.id
      );

      // Create updatedPlayers array based on prevPlayers
      const updatedPlayers = prevPlayers.map((p, index) =>
        index === currentPlayerIndex ? { ...p, factions: keptFactions, initialDraftFactions: [] } : p
      );

      // Add discard action to display history (using values from the current iteration's state)
      const newHistoryEntry = {
        player: getPlayerDisplayName(currentPlayer), // Use display name for history
        faction: discardedFaction.name, // This is the DISCARDED faction
        type: 'discarded',
        overallPick: currentOverallPick,
        keptFactionNames: keptFactions.map(f => f.name), // Store kept faction names for history display
        description: `Discarded ${discardedFaction.name} (Kept ${keptFactions[0].name} and ${keptFactions[1].name})`, // Still keep this for fallback
      };

      const newDisplayDraftHistory = [...displayDraftHistory, newHistoryEntry];
      setDisplayDraftHistory(newDisplayDraftHistory);

      setDraftChoices(null); // Clear choices locally within this update flow
      setHoveredFactionId(null); // Clear hovered state after a choice is made

      const nextOverallPick = currentOverallPick + 1;
      setCurrentOverallPick(nextOverallPick); // This will trigger a re-render

      // Check if all players have discarded
      if (nextOverallPick > updatedPlayers.length) { // Use updatedPlayers.length for clarity
        setDraftInProgress(false);
        setMessage('Discard 1 of 3 Draft complete!');
        // Store completed draft results
        setLastCompletedDraft({
          players: updatedPlayers,
          history: newDisplayDraftHistory, // Capture history including this pick
          mode: 'discard_1_of_3',
          numFactionsToPresent: numFactionsToPresent,
          unchosenFactionsOption: unchosenFactionsOption,
          snakeDraftNumFactionsToPresent: snakeDraftNumFactionsToPresent,
          snakeDraftUnchosenFactionsOption: snakeDraftUnchosenFactionsOption,
          useRandomSubsetForSnakeDraft: useRandomSubsetForSnakeDraft, // Save new option
          randomSubsetSize: randomSubsetSize, // Save new option
          useOfficialCustomRatio: useOfficialCustomRatio, // Save new option
          officialCustomRatio: officialCustomRatio, // Save new option
          useCustomData: useCustomData, // Save new option
        });
        setDraftStateSnapshots([]); // Clear snapshots once draft is completed and stored
      } else {
        // Get the next player's ID from the randomized order
        const nextPlayerId = playerTurnOrder.current[nextOverallPick - 1]; // Use playerTurnOrder
        // Find the next player in the *updatedPlayers* array (critical for correct data)
        const nextPlayer = updatedPlayers.find(p => p.id === nextPlayerId);

        if (nextPlayer) {
          setCurrentPlayerTurnIndex(updatedPlayers.findIndex(p => p.id === nextPlayerId));
          const choicesForNextPlayer = nextPlayer.initialDraftFactions;
          setDraftChoices(choicesForNextPlayer); // Present their initial factions for discard
          // Take snapshot AFTER state is updated for the next turn
          setDraftStateSnapshots(prev => [...prev, deepCopyState(updatedPlayers, currentDraftPool.current, updatedPlayers.findIndex(p => p.id === nextPlayerId), nextOverallPick, choicesForNextPlayer, newDisplayDraftHistory)]);
        } else {
          console.error("Error: Next player not found for advancing discard phase after state update.");
          setDraftInProgress(false);
          setDraftChoices(null);
          setMessage("Error advancing discard phase. Please reset.");
        }
      }
      return updatedPlayers; // Return the new state for `players`
    });
  };

  // NEW FUNCTION: Start a round for 'pick_2_of_X' mode
  const startPick2OfXRound = (pickNum, currentPlayersState) => {
    const numPlayers = currentPlayersState.length;
    const currentPlayerId = playerTurnOrder.current[pickNum]; // pickNum is 0-indexed for playerTurnOrder
    const currentPlayerIndex = currentPlayersState.findIndex(p => p.id === currentPlayerId);

    if (currentPlayerIndex === -1) {
        console.error("Error: Current player not found for pick 2 of X round.");
        setDraftInProgress(false);
        setDraftChoices(null);
        setMessage("Error starting Pick 2 of X round. Please reset.");
        return;
    }

    // Update state for the current turn
    setCurrentPlayerTurnIndex(currentPlayerIndex);
    setCurrentOverallPick(pickNum + 1); // Overall pick is 1-indexed
    setSelectedPick2OfXFactions([]); // Reset selected factions for the new player's turn!

    // Ensure enough factions are in the pool to draw X for this player
    if (currentDraftPool.current.length < numFactionsToPresent) {
        setMessage('Not enough factions left in the pool for this pick. Draft ended early.');
        setDraftInProgress(false);
        setDraftChoices(null);
        return;
    }

    // Draw X unique factions from the current draft pool
    const drawnFactions = [];
    // Create a temporary mutable copy of the pool for drawing
    const tempPool = [...currentDraftPool.current]; 

    for (let i = 0; i < numFactionsToPresent; i++) {
        if (tempPool.length === 0) {
            console.warn("Ran out of factions while drawing for Pick 2 of X.");
            break;
        }
        const randomIndex = Math.floor(RandoM.random() * tempPool.length); // Use RandoM.random()
        drawnFactions.push(tempPool.splice(randomIndex, 1)[0]);
    }
    setDraftChoices(drawnFactions); // Set the X choices to be displayed

    // Take snapshot AFTER state is updated for the current turn
    // If it's the very first pick (pickNum === 0 for this function), ensure history in snapshot is empty
    const historyForSnapshot = pickNum === 0 ? [] : displayDraftHistory;
    setDraftStateSnapshots(prev => [...prev, deepCopyState(currentPlayersState, currentDraftPool.current, currentPlayerIndex, pickNum + 1, drawnFactions, historyForSnapshot)]);
  };

  // NEW FUNCTION: Handle player's selection of 2 factions from X for 'pick_2_of_X' mode
  const handlePick2OfXChoice = (chosenFactions, currentPlayersState) => {
    const numPlayers = currentPlayersState.length;

    if (chosenFactions.length !== 2) {
        setMessage("Please select exactly two factions.");
        return;
    }

    // Validate that the chosen factions are actually from the current draftChoices
    const currentDraftChoicesIds = new Set(draftChoices.map(f => f.id));
    const areChosenFactionsValid = chosenFactions.every(f => currentDraftChoicesIds.has(f.id));

    if (!areChosenFactionsValid) {
        setMessage("Invalid selection. Please choose factions from the current set of options.");
        setSelectedPick2OfXFactions([]); // Clear invalid selections
        return;
    }


    const currentPlayerId = playerTurnOrder.current[currentOverallPick - 1];
    const currentPlayerIndex = currentPlayersState.findIndex(p => p.id === currentPlayerId);

    if (currentPlayerIndex === -1) {
        console.error("Error: Current player not found for pick 2 of X choice.");
        setDraftInProgress(false);
        setDraftChoices(null);
        setMessage("Error processing pick. Please reset.");
        return;
    }

    // Update the chosen player's factions
    const updatedPlayers = currentPlayersState.map((p, index) =>
      index === currentPlayerIndex ? { ...p, factions: chosenFactions } : p
    );
    setPlayers(updatedPlayers);

    // Determine unchosen factions
    const unchosenFactions = draftChoices.filter(
        (faction) => !chosenFactions.some(chosen => chosen.id === faction.id)
    );

    // Update the main draft pool based on the unchosenFactionsOption
    if (unchosenFactionsOption === 'remove_from_play') {
        // Remove all X factions that were presented from the main pool
        currentDraftPool.current = currentDraftPool.current.filter(
            (faction) => !draftChoices.some(presented => presented.id === faction.id)
        );
    } else { // 're_integrate'
        // Only remove the 2 chosen factions from the main pool; unchosen ones remain
        currentDraftPool.current = currentDraftPool.current.filter(
            (faction) => !chosenFactions.some(chosen => chosen.id === faction.id)
        );
    }

    // Add this pick to the display draft history
    const newHistoryEntry = {
      player: getPlayerDisplayName(currentPlayersState[currentPlayerIndex]),
      chosenFactions: chosenFactions.map(f => f.name),
      unchosenFactions: unchosenFactions.map(f => f.name),
      type: 'picked_2_of_X',
      overallPick: currentOverallPick,
      description: `${getPlayerDisplayName(currentPlayersState[currentPlayerIndex])} picked ${chosenFactions[0].name} and ${chosenFactions[1].name} from ${numFactionsToPresent} options.`,
    };
    const newDisplayDraftHistory = [...displayDraftHistory, newHistoryEntry];
    setDisplayDraftHistory(newDisplayDraftHistory);

    setDraftChoices(null); // Clear choices
    setHoveredFactionId(null); // Clear hovered state

    const nextOverallPick = currentOverallPick; // currentOverallPick is 1-indexed, matches player index in playerTurnOrder
    
    // Check if all players have made their picks (each player picks once in this mode)
    if (nextOverallPick >= numPlayers) {
      setDraftInProgress(false);
      setMessage('Pick 2 of X Draft complete!');
      // Store completed draft results
      setLastCompletedDraft({
        players: updatedPlayers,
        history: newDisplayDraftHistory, // Capture history including this pick
        mode: 'pick_2_of_X',
        numFactionsToPresent: numFactionsToPresent,
        unchosenFactionsOption: unchosenFactionsOption,
        snakeDraftNumFactionsToPresent: snakeDraftNumFactionsToPresent,
        snakeDraftUnchosenFactionsOption: snakeDraftUnchosenFactionsOption,
        useRandomSubsetForSnakeDraft: useRandomSubsetForSnakeDraft, // Save new option
        randomSubsetSize: randomSubsetSize, // Save new option
        useOfficialCustomRatio: useOfficialCustomRatio, // Save new option
        officialCustomRatio: officialCustomRatio, // Save new option
        useCustomData: useCustomData, // Save new option
      });
      setDraftStateSnapshots([]); // Clear snapshots once draft is completed and stored
    } else {
      // Advance to the next player's turn
      startPick2OfXRound(nextOverallPick, updatedPlayers); // Pass next pick index (0-indexed)
    }
  };

  // Function to undo the last draft action
  const handleUndoLastAction = () => {
    setDraftStateSnapshots(prevSnapshots => {
        if (prevSnapshots.length <= 1) { // If only the initial "ready for pick 1" snapshot is left
            // This means we are at the very beginning of the draft.
            // Resetting the app here is a valid user experience if they want to restart from scratch.
            resetApp(); 
            setMessage('Draft reset to initial state.');
            return []; // Clear snapshots
        }

        const newSnapshots = [...prevSnapshots];
        newSnapshots.pop(); // Remove the last snapshot (the state *after* the last action was completed)
        const previousSnapshot = newSnapshots[newSnapshots.length - 1]; // This is the state *before* the last action was taken

        // Restore state from the previous snapshot
        setPlayers(JSON.parse(JSON.stringify(previousSnapshot.players)));
        currentDraftPool.current = [...previousSnapshot.draftPool]; // Directly assign to ref's current
        setCurrentPlayerTurnIndex(previousSnapshot.currentPlayerIndex);
        setCurrentOverallPick(previousSnapshot.overallPick);
        setDraftChoices(previousSnapshot.draftChoices ? JSON.parse(JSON.stringify(previousSnapshot.draftChoices)) : null);
        setDisplayDraftHistory([...previousSnapshot.displayHistory]); // Restore display history

        // Ensure modal is open and draft is in progress
        setShowDraftResultsModal(true);
        setDraftInProgress(true);
        setMessage('Last action undone.');

        return newSnapshots; // Return the updated snapshots array
    });
  };


  // Reset the entire application state
  const resetApp = () => {
    console.log("Resetting app...");
    // Reset all component states to their defaults
    setPlayers(
      Array.from({ length: 4 }, (_, i) => ({
        id: i,
        name: '',
        factions: [],
        initialDraftFactions: []
      }))
    );
    setDraftInProgress(false);
    setDisplayDraftHistory([]);
    setDraftStateSnapshots([]);
    setShowDraftResultsModal(false);
    setMessage('');
    setDraftChoices(null);
    setCurrentPlayerTurnIndex(0);
    setCurrentOverallPick(0);
    currentDraftPool.current = [];
    playerTurnOrder.current = [];
    setDraftMode('snake_draft');
    setNumFactionsToPresent(6);
    setUnchosenFactionsOption('remove_from_play');
    setSnakeDraftNumFactionsToPresent(2);
    setSnakeDraftUnchosenFactionsOption('remove_from_play');
    setUseRandomSubsetForSnakeDraft(false);
    setRandomSubsetSize(0);
    setUseOfficialCustomRatio(false);
    setOfficialCustomRatio('1:1');
    setHoveredFactionId(null);
    setSelectedPick2OfXFactions([]);
    setFilterText('');
    setLastCompletedDraft(null);
    
    // Clear user preferences from localStorage
    clearSavedSelection();
    console.log("User preferences cleared from localStorage.");

    // Clear game data from localStorage
    localStorage.removeItem(SMASH_UP_GAME_DATA_STORAGE_KEY);
    console.log("Cleared saved game data from localStorage.");

    // Set useCustomData to false. This will trigger the useEffect for fetchGameData.
    setUseCustomData(false);
    console.log("useCustomData set to false.");

    // Immediately set smashUpData to default for quick UI update.
    setSmashUpData(DEFAULT_SMASH_UP_DATA);
    console.log("smashUpData set to DEFAULT_SMASH_UP_DATA.");

    // Explicitly re-initialize faction selection to default,
    // using the DEFAULT_SMASH_UP_DATA and assuming custom data is off.
    // This provides an immediate visual reset.
    initializeSelection(DEFAULT_SMASH_UP_DATA.sets, DEFAULT_SMASH_UP_DATA.factions, null, false);
    console.log("Faction selection re-initialized to default.");
  };

  // Toggle expansion of an accordion panel
  const toggleSetExpansion = (setId) => {
    setExpandedSets(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(setId)) {
        newExpanded.delete(setId);
      } else {
        newExpanded.add(setId);
      }
      return newExpanded;
    });
  };

  // Handle selection/deselection of factions in 'pick_2_of_X' modal
  const handlePick2OfXSelection = (faction) => {
    setSelectedPick2OfXFactions(prevSelected => {
      if (prevSelected.some(f => f.id === faction.id)) {
        // Faction is already selected, deselect it
        return prevSelected.filter(f => f.id !== faction.id);
      } else {
        // Faction is not selected, select it if less than 2 are already selected
        if (prevSelected.length < 2) {
          return [...prevSelected, faction];
        } else {
          // If 2 are already selected, replace the first one with the new one
          // Or, you could prevent selection: setMessage("You can only select 2 factions."); return prevSelected;
          return [prevSelected[1], faction]; // Replaces the oldest selection
        }
      }
    });
  };

  // Function to handle player count changes
  const setPlayerCount = useCallback((count) => {
    setPlayers(prevPlayers => {
      const newPlayers = Array.from({ length: count }, (_, i) => {
        // Reuse existing player names if available, otherwise create new empty player
        return prevPlayers[i] ? { ...prevPlayers[i], factions: [], initialDraftFactions: [] } : { id: i, name: '', factions: [], initialDraftFactions: [] };
      });
      return newPlayers;
    });
  }, []);

  // Function to handle player name changes
  const handlePlayerNameChange = useCallback((index, newName) => {
    setPlayers(prevPlayers => {
      const updatedPlayers = [...prevPlayers];
      updatedPlayers[index] = { ...updatedPlayers[index], name: newName };
      return updatedPlayers;
    });
  }, []);

  // Function to get player display name (either custom name or "Player X")
  const getPlayerDisplayName = useCallback((player) => {
    return player.name.trim() !== '' ? player.name : `Player ${player.id + 1}`;
  }, []);

  // Function to handle toggling a set (all factions within it)
  const handleSetToggle = useCallback((setId) => {
    setSelectedFactionsBySet(prevSelected => {
      const newSelected = { ...prevSelected };
      const allFactionsInSet = smashUpData.factions.filter(f => f.setId === setId);
      const currentSelectedInSet = prevSelected[setId] || new Set();
      
      console.log(`Toggling set ${setId}. All factions in set:`, allFactionsInSet.map(f => f.name));
      console.log("Currently selected in set:", Array.from(currentSelectedInSet).map(id => smashUpData.factions.find(f => f.id === id)?.name));

      if (allFactionsInSet.length === 0) return prevSelected; // No factions to toggle

      const areAllFactionsInSetSelected = allFactionsInSet.every(f => currentSelectedInSet.has(f.id));

      if (areAllFactionsInSetSelected) {
        // If all are selected, deselect all
        delete newSelected[setId]; // Remove the set entry entirely
        console.log(`Deselected all factions in set ${setId}.`);
      } else {
        // If some or none are selected, select all
        newSelected[setId] = new Set(allFactionsInSet.map(f => f.id));
        console.log(`Selected all factions in set ${setId}.`);
      }
      return newSelected;
    });
  }, [smashUpData.factions]);

  // Function to handle toggling an individual faction
  const handleFactionToggle = useCallback((setId, factionId) => {
    setSelectedFactionsBySet(prevSelected => {
      const newSelected = { ...prevSelected };
      const currentSelectedInSet = new Set(prevSelected[setId] || []); // Create a new Set from existing or empty

      if (currentSelectedInSet.has(factionId)) {
        currentSelectedInSet.delete(factionId);
        console.log(`Deselected faction ${factionId} in set ${setId}.`);
      } else {
        currentSelectedInSet.add(factionId);
        console.log(`Selected faction ${factionId} in set ${setId}.`);
      }

      if (currentSelectedInSet.size === 0) {
        delete newSelected[setId]; // Remove set if no factions are selected
        console.log(`Set ${setId} removed as no factions are selected.`);
      } else {
        newSelected[setId] = currentSelectedInSet;
        console.log(`Set ${setId} updated with new selection.`);
      }
      return newSelected;
    });
  }, []);

    // Memoized calculation for the disabled state of the "Begin Draft" button
    const isDraftButtonDisabled = useMemo(() => {
        if (draftInProgress || loadingData) return true;

        const numPlayers = players.length;
        const totalPicksNeeded = numPlayers * 2; // Each player picks 2 factions

        // Separate all available selected factions into official and custom
        const allOfficialFactions = availableFactionsForDraft.filter(f => !f.isCustom);
        const allCustomFactions = availableFactionsForDraft.filter(f => f.isCustom);

        if (draftMode === 'snake_draft') {
            if (useRandomSubsetForSnakeDraft && useOfficialCustomRatio) {
                // Combined mode: Random Subset with Official:Custom Ratio
                const ratioParts = officialCustomRatio.split(':').map(Number);
                if (ratioParts.length !== 2 || isNaN(ratioParts[0]) || isNaN(ratioParts[1]) || ratioParts[0] < 0 || ratioParts[1] < 0 || (ratioParts[0] === 0 && ratioParts[1] === 0)) {
                    return true; // Invalid ratio format
                }
                const [officialRatioVal, customRatioVal] = ratioParts;
                const ratioSum = officialRatioVal + customRatioVal;

                // Calculate the number of official/custom factions needed for the *subset size*
                let numOfficialInSubset = Math.round(randomSubsetSize * (officialRatioVal / ratioSum));
                let numCustomInSubset = randomSubsetSize - numOfficialInSubset;

                // Ensure randomSubsetSize is valid
                if (!Number.isInteger(randomSubsetSize) || randomSubsetSize < totalPicksNeeded || randomSubsetSize > availableFactionsForDraft.length) {
                    return true;
                }

                // Check if enough factions are available to form the subset with the desired ratio
                if (allOfficialFactions.length < numOfficialInSubset || allCustomFactions.length < numCustomInSubset) {
                    return true;
                }
                // Also ensure the calculated official + custom equals the desired subset size
                if (numOfficialInSubset + numCustomInSubset < randomSubsetSize) {
                    return true;
                }

            } else if (useRandomSubsetForSnakeDraft) {
                // Only Random Subset enabled
                const minRequiredForSubset = totalPicksNeeded;
                if (!Number.isInteger(randomSubsetSize) || randomSubsetSize < minRequiredForSubset || randomSubsetSize > availableFactionsForDraft.length) {
                    return true; // Invalid randomSubsetSize
                }
            } else if (useOfficialCustomRatio) { // This block will now only be reached if useRandomSubsetForSnakeDraft is false
                // Only Official : Custom Ratio enabled (no random subset)
                // This case should ideally not be reachable due to UI logic, but adding a check for robustness
                if (!smashUpData.factions.some(f => f.isCustom)) {
                    return true; // Cannot use ratio without custom data
                }

                const ratioParts = officialCustomRatio.split(':').map(Number);
                if (ratioParts.length !== 2 || isNaN(ratioParts[0]) || isNaN(ratioParts[1]) || ratioParts[0] < 0 || ratioParts[1] < 0 || (ratioParts[0] === 0 && ratioParts[1] === 0)) {
                    return true; // Invalid ratio format
                }
                const [officialRatioVal, customRatioVal] = ratioParts;
                const ratioSum = officialRatioVal + customRatioVal;

                let requiredOfficial = Math.round(totalPicksNeeded * (officialRatioVal / ratioSum));
                let requiredCustom = totalPicksNeeded - requiredOfficial;

                if (allOfficialFactions.length < requiredOfficial || allCustomFactions.length < requiredCustom) {
                    return true;
                }
                if (requiredOfficial + requiredCustom < totalPicksNeeded) {
                    return true;
                }
            } else {
                // Standard Snake Draft (no subset, no ratio)
                let requiredFactionsForPool = totalPicksNeeded;
                if (snakeDraftNumFactionsToPresent !== 'all' && snakeDraftUnchosenFactionsOption === 'remove_from_play') {
                    requiredFactionsForPool = numPlayers * 2 * snakeDraftNumFactionsToPresent;
                } else if (snakeDraftNumFactionsToPresent !== 'all' && snakeDraftUnchosenFactionsOption === 're_integrate') {
                    requiredFactionsForPool = (numPlayers * 2) + (snakeDraftNumFactionsToPresent - 1);
                } else if (snakeDraftNumFactionsToPresent === 'all') { // When 'all' is selected, it implicitly re-integrates
                    requiredFactionsForPool = numPlayers * 2;
                }
                if (availableFactionsForDraft.length < requiredFactionsForPool) {
                    return true;
                }
            }
        } else if (draftMode === 'discard_1_of_3') {
            const totalFactionsToDraw = numPlayers * 3;
            if (availableFactionsForDraft.length < totalFactionsToDraw) {
                return true;
            }
        } else if (draftMode === 'pure_random_assignment') {
            const totalFactionsToDraw = numPlayers * 2;
            if (availableFactionsForDraft.length < totalFactionsToDraw) {
                return true;
            }
        } else if (draftMode === 'pick_2_of_X') {
            const totalFactionsToDraw = (unchosenFactionsOption === 'remove_from_play')
                ? numPlayers * numFactionsToPresent
                : (numPlayers * 2) + (numFactionsToPresent - 2);
            if (availableFactionsForDraft.length < totalFactionsToDraw) {
                return true;
            }
        }
        return false;
    }, [draftInProgress, loadingData, players.length, draftMode, snakeDraftNumFactionsToPresent, snakeDraftUnchosenFactionsOption, numFactionsToPresent, unchosenFactionsOption, availableFactionsForDraft.length, useRandomSubsetForSnakeDraft, randomSubsetSize, useOfficialCustomRatio, officialCustomRatio, smashUpData.factions, availableFactionsForDraft]);


    // Memoized list of sets that have factions in the final draft results
    const setsInFinalDraft = useMemo(() => {
        if (!smashUpData.sets || !smashUpData.factions) return [];

        const uniqueSetIds = new Set();
        // Determine which player data to use: current if draftInProgress, otherwise lastCompletedDraft
        const dataToUse = draftInProgress ? players : (lastCompletedDraft ? lastCompletedDraft.players : []);

        dataToUse.forEach(player => {
            player.factions.forEach(faction => {
                uniqueSetIds.add(faction.setId);
            });
        });

        const sets = Array.from(uniqueSetIds)
            .map(setId => smashUpData.sets.find(set => set.id === setId))
            .filter(set => set != null) // Filter out any undefined sets if an ID wasn't found
            .sort((a, b) => a.name.localeCompare(b.name));

        return sets;
    }, [players, lastCompletedDraft, smashUpData.sets, smashUpData.factions, draftInProgress]);


  return (
	<ThemeProvider theme={darkTheme}> {/* Apply the custom dark theme */}
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 to-purple-800 text-white font-inter"> {/* Removed padding from here */}
      <div className="max-w-4xl mx-auto bg-gray-800 bg-opacity-90 rounded-xl shadow-2xl p-6 sm:p-8 space-y-8 border border-purple-600"> {/* Added vertical margin here */}

        {/* Header */}
        <h1 className="text-4xl sm:text-5xl font-extrabold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-300 pb-2 border-b-4 border-purple-700">
          Smash Up Randomizer
        </h1>
        <p className="text-center text-lg text-gray-300">
          Facilitate your next Smash Up game with randomized faction selection and multiple draft methods!
        </p>

        {/* Player Setup Section */}
        <div className="bg-gray-700 bg-opacity-70 p-6 rounded-lg shadow-inner border border-gray-600">
          <h2 className="text-2xl font-bold mb-4 text-purple-300">1. Player Setup</h2>
          {/* Player Count Buttons */}
          <div className="flex justify-center mb-6 space-x-4">
            {[2, 3, 4].map((count) => (
              <button
                key={count}
                onClick={() => setPlayerCount(count)}
                className={`px-4 py-2 text-base font-semibold rounded-lg shadow-md transition duration-200 transform hover:scale-105
                  sm:px-6 sm:py-3 sm:text-lg
                  ${
                    players.length === count
                      ? 'bg-purple-700 text-white'
                      : 'bg-gray-600 hover:bg-gray-700 text-gray-200'
                  }`}
              >
                {count} Players
              </button>
            ))}
          </div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {players.map((player, index) => (
                <TextField
                  key={player.id}
                  label={`Player ${index + 1}`}
                  value={player.name}
                  onChange={(e) => handlePlayerNameChange(index, e.target.value)}
                  fullWidth
                  sx={{
                    // Example of direct styling, but better managed by theme and global styles
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(17, 24, 39, 0.9)', // Tailwind gray-900 opacity 0.9
                    },
                  }}
                  // Removed className="rounded-md" as it's handled by MuiOutlinedInput-root styleOverrides
                />
              ))}
            </div>
        </div>

        {/* Set and Faction Selection Section - Redesigned with Accordion */}
        <div className="bg-gray-700 bg-opacity-70 p-6 rounded-lg shadow-inner border border-gray-600">
          <h2 className="text-2xl font-bold mb-4 text-purple-300">2. Select Sets & Factions</h2>

          {loadingData && <p className="text-center text-lg text-blue-300">Loading game data...</p>}
          {/* Display dataError message if there is an error, but don't block rendering of the sets/factions */}
          {dataError && <p className="text-center text-lg text-red-400">{dataError}</p>}

          {/* Render sets and factions if not loading AND smashUpData.sets is populated (even if from default) */}
          {!loadingData && smashUpData.sets.length > 0 && (
            <>
              <div className="max-h-96 overflow-y-auto pr-2 custom-scrollbar space-y-3 mb-4">
                {smashUpData.sets
                  .filter(set => set.factionCount > 0 && set.status === 'active')
                  .map((set) => {
                    const allFactionsInSet = smashUpData.factions.filter(f => f.setId === set.id);
                    const selectedFactionsInSet = selectedFactionsBySet[set.id] || new Set();
                    
                    const areAllFactionsInSetSelected = allFactionsInSet.length > 0 && allFactionsInSet.every(f => selectedFactionsInSet.has(f.id));
                    const areSomeFactionsInSetSelected = selectedFactionsInSet.size > 0 && !areAllFactionsInSetSelected;

                    const isSetExpanded = expandedSets.has(set.id);

                    return (
                      <div key={set.id} className="border border-gray-600 rounded-md bg-gray-700">
                        <div
                          className="flex items-center justify-between p-3 cursor-pointer select-none"
                          onClick={() => toggleSetExpansion(set.id)}
                        >
                          <label className="flex items-center space-x-2 text-gray-100 text-lg font-semibold cursor-pointer">
                            <input
                              type="checkbox"
                              checked={areAllFactionsInSetSelected}
                              // Use a ref callback to imperatively set the indeterminate property
                              ref={el => {
                                if (el) {
                                  el.indeterminate = areSomeFactionsInSetSelected;
                                }
                              }}
                              onChange={(e) => {
                                e.stopPropagation(); // Prevent accordion from toggling when checkbox is clicked
                                handleSetToggle(set.id);
                              }}
                              // Added a conditional class for indeterminate state
                              className={`form-checkbox h-5 w-5 rounded border-gray-500 focus:ring-purple-400 transition duration-150 ease-in-out
                                ${areSomeFactionsInSetSelected ? 'indeterminate-checkbox' : 'checked-checkbox'}
                              `}
                            />
                            <span>{set.name}</span>
                          </label>
                          <span className="text-gray-400 text-xl">
                            {isSetExpanded ? '▲' : '▼'}
                          </span>
                        </div>
                        {isSetExpanded && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 px-6 pb-3 border-t border-gray-600 pt-3 animate-fade-in">
                            {smashUpData.factions
                              .filter(faction => faction.setId === set.id)
                              .map(faction => (
                                <label key={faction.id} className="flex items-center space-x-2 text-gray-300 cursor-pointer text-base">
                                  <input
                                    type="checkbox"
                                    checked={selectedFactionsBySet[faction.setId]?.has(faction.id) || false}
                                    onChange={() => handleFactionToggle(faction.setId, faction.id)}
                                    className="form-checkbox h-4 w-4 text-pink-500 rounded border-gray-500 focus:ring-pink-400 transition duration-150 ease-in-out checked-checkbox"
                                  />
                                  <span>{faction.name} {faction.isCustom && <span className="text-xs text-blue-400">(Custom)</span>}</span>
                                </label>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
              {/* Select All / Select None / Custom Factions Toggle */}
              <div className="flex flex-col sm:flex-row justify-center mb-6 space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => handleSelectAllSetsAndFactions(smashUpData.sets, smashUpData.factions)}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-md transition duration-200 transform hover:scale-105"
                >
                  Select All
                </button>
                <button
                  onClick={handleSelectNoneSetsAndFactions}
                  className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg shadow-md transition duration-200 transform hover:scale-105"
                >
                  Select None
                </button>
                {/* NEW: Custom Factions Toggle Switch */}
                <label className="inline-flex items-center cursor-pointer p-3 rounded-lg">
                  <input
                    type="checkbox"
                    value=""
                    className="sr-only peer"
                    checked={useCustomData}
                    onChange={(e) => {
                      setUseCustomData(e.target.checked);
                      // Save preferences immediately on toggle
                      saveCurrentSelection(); // This will save the NEW useCustomData state
                    }}
                  />
                  <div className="relative w-14 h-7 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-[28px] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
                  <span className="ml-3 text-lg font-medium text-gray-200">Custom Factions</span>
                </label>
              </div>
            </>
          )}
          <p className="text-sm text-gray-400 mt-4">
            Total factions available for draft: <span className="font-semibold text-purple-300">{availableFactionsForDraft.length}</span>
          </p>
          {/* New line for excluded factions with conditional display and tooltip */}
          {allActiveFactionsCount > 0 && excludedFactions.length > 0 && (
            <p className="text-sm text-gray-400 mt-2 relative group cursor-help">
              Factions excluded: <span className="font-semibold text-red-300">{excludedFactions.length}</span>
              {/* Conditional direct list display */}
              {excludedFactions.length < 20 && excludedSummaryList.length > 0 && (
                <span className="ml-1 text-gray-300">
                    {excludedSummaryList.map((item, index) => (
                      <React.Fragment key={index}>
                        <span className={item.type === 'set' ? 'text-yellow-300' : 'text-red-400'}>
                          {item.name}
                        </span>
                        {index < excludedSummaryList.length - 1 && ', '}
                      </React.Fragment>
                    ))}
                  </span>
              )}
              {/* Tooltip always shown on hover if any excluded factions */}
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-2 bg-gray-700 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-max max-w-xs z-10 hidden group-hover:block pointer-events-none">
                {excludedFactions.map(f => f.name).join(', ')}
              </span>
            </p>
          )}
        </div>


        {/* Draft Mode Selection Section */}
        <div className="bg-gray-700 bg-opacity-70 p-6 rounded-lg shadow-inner border border-gray-600">
          <h2 className="text-2xl font-bold mb-4 text-purple-300">3. Choose Mode</h2>
          <div className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-6">
            <button
              onClick={() => setDraftMode('snake_draft')}
              className={`px-6 py-3 font-semibold rounded-lg shadow-md transition duration-200 transform hover:scale-105 ${
                draftMode === 'snake_draft'
                  ? 'bg-purple-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-gray-200'
              }`}
            >
              Snake Draft
            </button>
            {/* NEW BUTTON for Pick 2 of X */}
            <button
              onClick={() => setDraftMode('pick_2_of_X')}
              className={`px-6 py-3 font-semibold rounded-lg shadow-md transition duration-200 transform hover:scale-105 ${
                draftMode === 'pick_2_of_X'
                  ? 'bg-purple-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-gray-200'
              }`}
            >
              Pick 2 of X
            </button>
          	<button
              onClick={() => setDraftMode('discard_1_of_3')}
              className={`px-6 py-3 font-semibold rounded-lg shadow-md transition duration-200 transform hover:scale-105 ${
                draftMode === 'discard_1_of_3'
                  ? 'bg-purple-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-gray-200'
              }`}
            >
              Discard 1 of 3
            </button>
            <button
              onClick={() => setDraftMode('pure_random_assignment')}
              className={`px-6 py-3 font-semibold rounded-lg shadow-md transition duration-200 transform hover:scale-105 ${
                draftMode === 'pure_random_assignment'
                  ? 'bg-purple-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-gray-200'
              }`}
            >
              Pure Random Assignment
            </button>
          </div>

          {/* Options for Snake Draft mode */}
          {draftMode === 'snake_draft' && (
            <div className="mt-6 p-4 bg-gray-600 rounded-lg border border-gray-500 animate-fade-in">
              <h3 className="text-xl font-bold mb-3 text-purple-200">Snake Draft Options</h3>
              
              {/* Option to use Random Subset */}
              <div className="mb-4">
                <label className="inline-flex items-center text-gray-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useRandomSubsetForSnakeDraft}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setUseRandomSubsetForSnakeDraft(isChecked);

                      if (isChecked) {
                        // If turning Random Subset ON
                        setSnakeDraftNumFactionsToPresent('all');
                        setSnakeDraftUnchosenFactionsOption('re_integrate');
                      } else {
                        // If turning Random Subset OFF
                        setUseOfficialCustomRatio(false); // Turn off ratio option
                        setOfficialCustomRatio('1:1'); // Reset ratio value
                      }
                    }}
                    className="form-checkbox h-5 w-5 text-purple-600 checked-checkbox"
                  />
                  <span className="ml-2 text-lg font-medium">Use Random Subset for Draft Pool</span>
                </label>
              </div>

              {/* Desired Subset Size input, shown conditionally */}
              {useRandomSubsetForSnakeDraft && (
                <div className="mb-4 animate-fade-in">
                  <TextField
                    label="Desired Subset Size"
                    type="number"
                    value={randomSubsetSize === 0 ? '' : randomSubsetSize} // Display empty string if 0 for better UX
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string temporarily for user input, but convert to 0 for state
                      setRandomSubsetSize(value === '' ? 0 : Math.max(0, parseInt(value) || 0));
                    }}
                    fullWidth
                    inputProps={{
                        min: players.length * 2, // Minimum factions needed for a full draft
                        max: availableFactionsForDraft.length, // Maximum available factions
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(17, 24, 39, 0.9)' },
                    }}
                  />
                  <p className="text-sm text-gray-400 mt-1">
                    Enter the total number of factions to randomly select from your chosen sets to form the draft pool.
                    (Min: {players.length * 2}, Max: {availableFactionsForDraft.length})
                  </p>
                </div>
              )}

              {/* NEW: Official : Custom Ratio Option - Only shown if useRandomSubsetForSnakeDraft is true */}
              {useRandomSubsetForSnakeDraft && (
                <div className="mb-4 animate-fade-in">
                  <label className="inline-flex items-center text-gray-200 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useOfficialCustomRatio}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setUseOfficialCustomRatio(isChecked);
                        // Force these settings if turning Official:Custom Ratio ON
                        if (isChecked) {
                          setSnakeDraftNumFactionsToPresent('all');
                          setSnakeDraftUnchosenFactionsOption('re_integrate');
                        }
                      }}
                      className="form-checkbox h-5 w-5 text-purple-600 checked-checkbox"
                    />
                    <span className="ml-2 text-lg font-medium">Use Official : Custom Faction Ratio</span>
                  </label>
                </div>
              )}

              {/* Official : Custom Ratio Input, shown conditionally */}
              {useOfficialCustomRatio && useRandomSubsetForSnakeDraft && (
                <div className="mb-4 animate-fade-in">
                  <TextField
                    label="Official : Custom Ratio (e.g., 3:5)"
                    type="text"
                    value={officialCustomRatio}
                    onChange={(e) => setOfficialCustomRatio(e.target.value)}
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(17, 24, 39, 0.9)' },
                    }}
                  />
                  <p className="text-sm text-gray-400 mt-1">
                    Defines the ratio of official factions to custom factions in the draft pool.
                    e.g., '1:1' for equal, '3:1' for more official, '1:3' for more custom.
                  </p>
                  {/* Reminder if no custom data is loaded */}
                  {useOfficialCustomRatio && !hasCustomFactionsLoaded && (
                    <p className="text-sm text-red-400 mt-1 font-semibold">
                      Import Custom Data to use this option.
                    </p>
                  )}
                </div>
              )}


              {/* Number of Factions to Present (1 of X) - Hidden if random subset or ratio is active */}
              {!(useRandomSubsetForSnakeDraft || useOfficialCustomRatio) && (
                <div className="mb-4 animate-fade-in">
                  <label htmlFor="snakeDraftNumFactionsToPresent" className="block text-gray-300 text-lg font-medium mb-2">
                    Number of factions presented (1 of X):
                  </label>
                  <select
                    id="snakeDraftNumFactionsToPresent"
                    value={snakeDraftNumFactionsToPresent}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSnakeDraftNumFactionsToPresent(value === 'all' ? 'all' : parseInt(value));
                      // If 'all' is selected, force re_integrate
                      if (value === 'all') {
                        setSnakeDraftUnchosenFactionsOption('re_integrate');
                      }
                    }}
                    className="block w-full p-2 rounded-md bg-gray-700 border border-gray-500 text-white focus:ring-purple-500 focus:border-purple-500"
                  >
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                    <option value="all">All</option>
                  </select>
                </div>
              )}

              {/* Unchosen Factions Option - Hidden if random subset, ratio, OR 'all' is active */}
              {!(useRandomSubsetForSnakeDraft || useOfficialCustomRatio || snakeDraftNumFactionsToPresent === 'all') && (
                <div className="animate-fade-in">
                  <span className="block text-gray-300 text-lg font-medium mb-2">
                    Unchosen factions:
                  </span>
                  <div className="flex space-x-4">
                    <label className="inline-flex items-center text-gray-200 cursor-pointer">
                      <input
                        type="radio"
                        name="snakeUnchosenFactions"
                        value="remove_from_play"
                        checked={snakeDraftUnchosenFactionsOption === 'remove_from_play'}
                        onChange={(e) => setSnakeDraftUnchosenFactionsOption(e.target.value)}
                        className="form-radio h-5 w-5 text-purple-600 checked-checkbox"
                      />
                      <span className="ml-2">Remove from Play</span>
                    </label>
                    <label className="inline-flex items-center text-gray-200 cursor-pointer">
                      <input
                        type="radio"
                        name="snakeUnchosenFactions"
                        value="re_integrate"
                        checked={snakeDraftUnchosenFactionsOption === 're_integrate'}
                        onChange={(e) => setSnakeDraftUnchosenFactionsOption(e.target.value)}
                        className="form-radio h-5 w-5 text-purple-600 checked-checkbox"
                      />
                      <span className="ml-2">Re-integrate into Pool</span>
                    </label>
                  </div>
                </div>
              )}
              {(useRandomSubsetForSnakeDraft || useOfficialCustomRatio || snakeDraftNumFactionsToPresent === 'all') && (
                <p className="text-sm text-gray-400 mt-2">
                    When "Random Subset", "Official : Custom Ratio", or "All" factions are selected, unchosen factions are always re-integrated into the pool.
                </p>
              )}
            </div>
          )}

          {/* Options for Pick 2 of X mode */}
          {draftMode === 'pick_2_of_X' && (
            <div className="mt-6 p-4 bg-gray-600 rounded-lg border border-gray-500 animate-fade-in">
              <h3 className="text-xl font-bold mb-3 text-purple-200">Pick 2 of X Options</h3>
              
              {/* Number of Factions to Present (X) */}
              <div className="mb-4">
                <label htmlFor="numFactionsToPresent" className="block text-gray-300 text-lg font-medium mb-2">
                  Number of factions presented (2 of X):
                </label>
                <select
                  id="numFactionsToPresent"
                  value={numFactionsToPresent}
                  onChange={(e) => setNumFactionsToPresent(parseInt(e.target.value))}
                  className="block w-full p-2 rounded-md bg-gray-700 border border-gray-500 text-white focus:ring-purple-500 focus:border-purple-500"
                >
                  {[4, 5, 6, 7, 8, 9, 10].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>

              {/* Unchosen Factions Option */}
              <div>
                <span className="block text-gray-300 text-lg font-medium mb-2">
                  Unchosen factions:
                </span>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center text-gray-200 cursor-pointer">
                    <input
                      type="radio"
                      name="unchosenFactions"
                      value="remove_from_play"
                      checked={unchosenFactionsOption === 'remove_from_play'}
                      onChange={(e) => setUnchosenFactionsOption(e.target.value)}
                      className="form-radio h-5 w-5 text-purple-600 checked-checkbox"
                    />
                    <span className="ml-2">Remove from Play</span>
                  </label>
                  <label className="inline-flex items-center text-gray-200 cursor-pointer">
                    <input
                      type="radio"
                      name="unchosenFactions"
                      value="re_integrate"
                      checked={unchosenFactionsOption === 're_integrate'}
                      onChange={(e) => setUnchosenFactionsOption(e.target.value)}
                      className="form-radio h-5 w-5 text-purple-600 checked-checkbox"
                    />
                    <span className="ml-2">Re-integrate into Pool</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Draft Controls */}
        <div className="bg-gray-700 bg-opacity-70 p-6 rounded-lg shadow-inner border border-gray-600 text-center">
          <h2 className="text-2xl font-bold mb-4 text-purple-300">4. Start Draft</h2>
          {message && (
            <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-sm font-medium">
              {message}
            </div>
          )}
          <button
            onClick={startDraft}
            className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xl font-bold rounded-lg shadow-xl transition duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-purple-500"
            disabled={isDraftButtonDisabled}
          >
            {draftInProgress ? 'Drafting...' : 'Begin Draft'}
          </button>
          {lastCompletedDraft && (
            <button
              onClick={handleReopenLastDraft}
              className="w-full md:w-auto px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white text-xl font-bold rounded-lg shadow-xl transition duration-300 transform hover:scale-105 active:scale-95 mt-4 md:ml-4 focus:outline-none focus:ring-4 focus:ring-blue-500"
            >
              View Last Draft Results
            </button>
          )}
          <button
            onClick={resetApp}
            className="w-full md:w-auto px-8 py-4 bg-gray-500 hover:bg-gray-600 text-white text-xl font-bold rounded-lg shadow-xl transition duration-300 transform hover:scale-105 active:scale-95 mt-4 md:ml-4 focus:outline-none focus:ring-4 focus:ring-gray-400"
          >
            Reset All
          </button>
        </div>

        {/* Draft Results Modal */}
        {showDraftResultsModal && (
          <div style={{ marginTop: '0' }} className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-gray-800 rounded-xl shadow-2xl p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-purple-600 relative" style={{ marginTop: 0 }}>
              {/* Close Button (X) */}
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl font-bold p-2 rounded-full hover:bg-gray-700 transition duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                aria-label="Close draft results"
              >
                &times; {/* HTML entity for 'times' or 'X' */}
              </button>

              <h2 className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-300 mb-6">
                Draft Results
                {!draftInProgress && draftMode === 'pure_random_assignment' && " (Random Assignment Complete)"}
                {!draftInProgress && draftMode === 'snake_draft' && " (Snake Draft Complete)"}
                {!draftInProgress && draftMode === 'discard_1_of_3' && " (Discard Phase Complete)"}
                {!draftInProgress && draftInProgress && draftMode === 'pick_2_of_X' && " (Pick 2 of X Draft Complete)"}
              </h2>

              {draftInProgress && (
                <div className="text-center mb-6">
                {/* Aligning color to match Player color */}
                  <p className="text-xl text-purple-300 mb-2">
                    <span className="font-bold text-purple-300">
                        {getPlayerDisplayName(players[currentPlayerTurnIndex])}
                    </span>'s Turn!
                  </p>
                  {draftMode === 'snake_draft' && (
                    <p className="text-lg text-gray-400">
                      Pick <span className="font-semibold text-purple-300">{currentOverallPick}</span> of <span className="font-semibold text-purple-300">{players.length * 2}</span>
                    </p>
                  )}
                  {draftMode === 'discard_1_of_3' && (
                    <p className="text-lg text-gray-400">
                      Discarding Round: <span className="font-semibold text-purple-300">{currentOverallPick}</span> of <span className="font-semibold text-purple-300">{players.length}</span> {/* Removed "Player" */}
                    </p>
                  )}
                  {draftMode === 'pick_2_of_X' && (
                    <p className="text-lg text-gray-400">
                      Picking Round: <span className="font-semibold text-purple-300">{currentOverallPick}</span> of <span className="font-semibold text-purple-300">{players.length}</span>
                    </p>
                  )}
                </div>
              )}

              {/* Faction Choices for Current Player - Only shown for 'snake_draft' mode */}
              {draftChoices && draftInProgress && draftMode === 'snake_draft' && (
                <div className="mt-4 text-center">
                  <p className="text-xl font-bold mb-4 text-green-400">
                    Choose your faction:
                  </p>
                  {(snakeDraftNumFactionsToPresent === 'all' || useRandomSubsetForSnakeDraft || useOfficialCustomRatio) && (
                    <div className="mb-4">
                      <TextField
                        label="Filter Factions"
                        variant="outlined"
                        fullWidth
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                borderRadius: '0.5rem',
                            },
                            '& .MuiInputBase-input': {
                                color: '#e2e8f0',
                            },
                            '& .MuiInputLabel-root': {
                                color: '#94a3b8',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: '#8b5cf6',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#4b5563',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#a78bfa !important',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#8b5cf6 !important',
                            },
                        }}
                      />
                    </div>
                  )}
                  <div className={`grid gap-4 ${
                    snakeDraftNumFactionsToPresent === 'all' || useOfficialCustomRatio || useRandomSubsetForSnakeDraft
                      ? 'grid-cols-5' // Changed from grid-cols-10 to grid-cols-5, applies to combined mode too
                      : draftChoices.length % 2 === 0
                        ? 'grid-cols-2'
                        : 'grid-cols-3'
                  }`}>
                    {draftChoices
                      .filter(f => f.name.toLowerCase().includes(filterText.toLowerCase()))
                      .map((faction) => (
                      <button
                        key={faction.id}
                        onClick={() => handleFactionChoice(faction, players)}
                        onMouseEnter={() => setHoveredFactionId(faction.id)}
                        onMouseLeave={() => setHoveredFactionId(null)}
                        className={`
                          flex flex-col items-center justify-center text-center px-6 py-2 text-white font-semibold rounded-lg shadow-md transition duration-200 transform hover:scale-105 focus:outline-none focus:ring-4
                          ${
                            hoveredFactionId === faction.id // This button is hovered (chosen)
                              ? 'bg-green-700 focus:ring-green-500'
                              : hoveredFactionId !== null // Another button is hovered (not chosen)
                                ? 'bg-red-700 focus:ring-red-500'
                                : 'bg-blue-600 focus:ring-blue-500' // No button is hovered
                          }
                        `}
                      >
                        <span className="text-lg">{faction.name} {faction.isCustom && <span className="text-xs text-blue-200">(Custom)</span>}</span>
                        {!(useRandomSubsetForSnakeDraft || useOfficialCustomRatio || snakeDraftNumFactionsToPresent === 'all') && ( // Conditionally hide description only if not combined/all
                          <span className="text-sm text-white mt-1 italic">{faction.description}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Faction Discard Choices for Current Player - Only shown for 'discard_1_of_3' mode */}
              {draftChoices && draftInProgress && draftMode === 'discard_1_of_3' && (
                <div className="mt-4 text-center">
                  <p className="text-xl font-bold mb-4">
                    <span className="text-green-400">Choose one faction to </span>
                    <span className="text-red-400">DISCARD:</span>
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"> {/* Applied grid classes here */}
                    {draftChoices.map((faction) => (
                      <button
                        key={faction.id}
                        onClick={() => handleDiscardChoice(faction)}
                        onMouseEnter={() => setHoveredFactionId(faction.id)}
                        onMouseLeave={() => setHoveredFactionId(null)}
                        className={`
                          flex flex-col items-center text-center px-6 py-3 text-white font-semibold rounded-lg shadow-md transition duration-200 transform hover:scale-105 focus:outline-none focus:ring-4
                          ${
                            hoveredFactionId === faction.id // This button is hovered
                              ? 'bg-red-700 focus:ring-red-500'
                              : hoveredFactionId !== null // Another button is hovered
                                ? 'bg-green-600 focus:ring-green-500'
                                : 'bg-blue-600 focus:ring-blue-500' // No button is hovered
                          }
                        `}
                      >
                        <span className="text-lg">{faction.name} {faction.isCustom && <span className="text-xs text-blue-200">(Custom)</span>}</span>
                        <span className="text-sm text-white mt-1 italic">{faction.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* NEW: Faction Choices for Current Player - Only shown for 'pick_2_of_X' mode */}
              {draftChoices && draftInProgress && draftMode === 'pick_2_of_X' && (
                <div className="mt-4 text-center">
                  <p className="text-xl font-bold mb-4 text-green-400">
                    Select two factions to keep:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {draftChoices.map((faction) => {
                      const isSelected = selectedPick2OfXFactions.some(f => f.id === faction.id);
                      return (
                        <button
                          key={faction.id}
                          onClick={() => handlePick2OfXSelection(faction)}
                          className={`
                            flex flex-col items-center text-center p-4 text-white font-semibold rounded-lg shadow-md transition duration-200 transform hover:scale-105 focus:outline-none focus:ring-4
                            ${isSelected
                              ? 'bg-purple-700 ring-purple-500' // Selected
                              : selectedPick2OfXFactions.length === 2
                                ? 'bg-gray-600 opacity-70 cursor-not-allowed' // Max selected, unselectable
                                : 'bg-blue-600 hover:bg-blue-700 ring-blue-500' // Available
                            }
                          `}
                          disabled={!isSelected && selectedPick2OfXFactions.length === 2}
                        >
                          <span className="text-lg">{faction.name} {faction.isCustom && <span className="text-xs text-blue-200">(Custom)</span>}</span>
                          <span className="text-sm text-white mt-1 italic">{faction.description}</span>
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => handlePick2OfXChoice(selectedPick2OfXFactions, players)}
                    disabled={selectedPick2OfXFactions.length !== 2 || !selectedPick2OfXFactions.every(f => draftChoices.some(dc => dc.id === f.id))} // Ensure selected factions are from current choices
                    className="mt-6 px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg shadow-md transition duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Confirm Selection
                  </button>
                </div>
              )}

              {/* Display Player Drafted Factions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 my-6">
                {(draftInProgress ? players : (lastCompletedDraft ? lastCompletedDraft.players : [])).map((player) => {
                  const isCurrentPlayer = (draftInProgress && (draftMode === 'snake_draft' || draftMode === 'discard_1_of_3' || draftMode === 'pick_2_of_X') && player.id === players[currentPlayerTurnIndex]?.id);
                  return (
                    <div key={player.id} className={`bg-gray-700 rounded-md p-4 border ${isCurrentPlayer ? 'border-purple-400 ring-2 ring-purple-400 shadow-lg' : 'border-gray-600'} transition-all duration-300 ease-in-out`}>
                      <h3 className="text-xl font-semibold text-yellow-300 mb-3 border-b border-gray-500 pb-2">
                          {getPlayerDisplayName(player)}
                      </h3>
                      {player.factions.length > 0 ? (
                        <ul className="list-disc list-inside space-y-1">
                          {player.factions.map((faction) => (
                            <li key={faction.id} className="text-lg text-green-400">
                              {faction.name} {faction.isCustom && <span className="text-xs text-blue-200">(Custom)</span>}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-400 text-sm italic">
                          {draftMode === 'discard_1_of_3' && player.initialDraftFactions.length > 0 && draftInProgress
                            ? 'Awaiting discard selection...'
                            : (draftMode === 'pick_2_of_X' || draftMode === 'snake_draft') && draftInProgress
                                ? 'Awaiting faction selection...'
                                : 'No factions picked yet.'}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Sets in Final Draft Results */}
              {setsInFinalDraft.length > 0 && (
                <div className="mt-6 bg-gray-700 rounded-md p-4 border border-gray-600">
                  <h3 className="text-xl font-semibold text-purple-300 mb-3 border-b border-gray-500 pb-2">
                    Sets Used in Draft
                  </h3>
                  <ul className="list-disc list-inside text-sm max-h-40 overflow-y-auto custom-scrollbar">
                    {setsInFinalDraft.map((set) => (
                      <li key={set.id} className="text-gray-200">
                        {set.name}
                      </li>
                    ))}
                  
                     {setsInFinalDraft.some(set => set.name === "The Obligatory Cthulhu Set") && (
                    <li className="text-green-200">Madness Deck</li>
                  )}
                     {setsInFinalDraft.some(set => set.name === "Smash Up: Munchkin") && (
                    <>
                      <li className="text-red-400">Monsters Deck</li>
                      <li className="text-yellow-300">Treasures Deck</li>
                    </>
                  )}
                  </ul>
                </div>
              )}

              {/* Draft History Display */}
              {(draftInProgress ? displayDraftHistory : (lastCompletedDraft ? lastCompletedDraft.history : [])).length > 0 && (
                <div className="mt-6 bg-gray-700 rounded-md p-4 border border-gray-600">
                  <h3 className="text-xl font-semibold text-purple-300 mb-3 border-b border-gray-500 pb-2">
                    {draftMode === 'discard_1_of_3' ? 'Discard History' : 'Draft History'}
                  </h3>
                  <ul className="list-disc list-inside text-sm max-h-40 overflow-y-auto custom-scrollbar">
                    {(draftInProgress ? displayDraftHistory : (lastCompletedDraft ? lastCompletedDraft.history : [])).map((pick, index) => (
                      <li key={index} className="text-gray-200">
                        {pick.type === 'discarded' ? (
                          <>
                            Round {pick.overallPick} ({pick.player}): <span className="text-red-400">Discarded {pick.faction}</span>
                            {pick.keptFactionNames && pick.keptFactionNames.length === 2 && (
                              <> (Kept <span className="text-green-400">{pick.keptFactionNames[0]}</span> and <span className="text-green-400">{pick.keptFactionNames[1]}</span>)</>
                            )}
                          </>
                        ) : pick.type === 'assigned' ? (
                          <>
                            Pick {pick.overallPick}: {pick.player} was randomly assigned <span className="text-green-400">{pick.faction}</span>
                          </>
                        ) : pick.type === 'picked_2_of_X' ? (
                          <>
                            Round {pick.overallPick}: {pick.player} picked <span className="text-green-400">{pick.chosenFactions.join(' and ')}</span> from {numFactionsToPresent} options.
                            {pick.unchosenFactions && pick.unchosenFactions.length > 0 && unchosenFactionsOption === 'remove_from_play' && (
                                <> (<span className="text-red-400">Discarded: {pick.unchosenFactions.join(', ')}</span>)</>
                            )}
                          </>
                        ) : ( // pick.type === 'picked' (snake draft)
                          <>
                            Pick {pick.overallPick}: {pick.player} chose <span className="text-green-400">{pick.faction}</span> from {pick.numPresented} options.
                            {pick.discardedFactionNames && pick.discardedFactionNames.length > 0 && (
                              <> (<span className="text-red-400">Discarded: {pick.discardedFactionNames.join(', ')}</span>)</>
                            )}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Undo and Close Buttons for the modal */}
              <div className="text-center mt-6 flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
                {draftInProgress && (draftMode !== 'pure_random_assignment') && (
                    <button
                        onClick={handleUndoLastAction}
                        disabled={draftStateSnapshots.length <= 1} // Disable if only initial snapshot exists
                        className="px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg shadow-md transition duration-200 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Undo Last Pick
                    </button>
                )}
                <button
                  onClick={handleCloseModal}
                  className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg shadow-md transition duration-200 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-red-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      {/* Ko-fi Link */}
      <div className="text-center mt-8 pb-4">
        <a
          href="https://ko-fi.com/liukus"
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-400 hover:text-purple-300 text-sm transition duration-200"
        >
          Support me on Ko-fi! <span role="img" aria-label="coffee">☕</span>
        </a>
      </div>
      {/* Custom Scrollbar Styling */}
      <style>{`
        /* Ensure html and body take full height and prevent default margins */
        html, body, #root {
          height: 100%;
          margin: 0;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: #4a5568; /* Tailwind gray-700 */
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #8b5cf6; /* Tailwind purple-500 */
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a78bfa; /* Tailwind purple-400 */
        }
        /* Fade-in animation for expanded content */
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }\
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }

        /* Styling for checked checkboxes */
        input[type="checkbox"].checked-checkbox {
          accent-color: rgb(126 34 206); /* Tailwind purple-700 */
        }

        /* Styling for indeterminate checkbox state */
        input[type="checkbox"].indeterminate-checkbox {
          accent-color: rgb(216 180 254); /* Tailwind purple-200 */
        }

        /* Fix for modal background not covering top 40px */
        .fixed.inset-0 {
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
        }
      `}</style>
    </div>
	</ThemeProvider>
  );
};

export default App;